/* Lightbox windows: the "mix" look (the owner's pick of 2026-10-15; branch ui-prototype, frontend/src/prototype/mix/BRIEF.md) folded into
   the real app from the UI prototype (shared/engine.css = the window engine, mix/windows.css = the skin;
   the Win window's celebration is win.css, loaded after this file by WindowFrame.tsx). Tokens: app/tokens.css
   (:root); the window tiers below set their own on .lbx_window, never on :root. Icons: the --i-<name>
   masks ui/icons.ts writes on :root.

   Layout of this file: (1) the legacy structural rules the skin relies on (ids / classes / texts the e2e
   suite reads, the half-pixel-free centring, the .skin_item img.card reset, the drag transform with
   --drag-x / --drag-y, the scrollers, the no-ads variants), (2) the engine (frame flex chain, scrollers,
   sticky action rows, static overlay, container entrance / close under WINDOW_CLOSE_MS, the desktop tier
   above the ad band, the phone sheet tier with the band reserved inside and --vvh), (3) the skin (header,
   calm close X, flat tabs, 48 / 54 px option rows with SVG marks and a neutral selection, 44 px buttons,
   forms, the ad boxes, the grown desktop sizes, the sheet tier). The legacy glow / gradient / bevel blocks,
   the PNG marks, the cup JPEG, the text-shadows and the fixed inner heights are gone; the cascade order
   inside each section is the prototype's (same selectors, the html[data-proto] prefix stripped).

   Contract (prototype PLAN.md "Keeping the e2e contract"): no class on .lbx_window, no new elements but
   the Win window's headline / subline (WinWindow.tsx), the box is opacity 1 and identity-transformed
   from the first frame, the overlay is static (#000, 0.3, z 9991), .lbx_header is #91b57b exact,
   .games_window / .tab_contents / .stats_table_cont stay the scrollers, phones keep box = viewport,
   the close stays <= 400 ms. */

/* ==== 1. legacy structure ====================================================================== */

/* Legacy had `* { margin: 0; padding: 0; border: none }`; the new app has no reset, so the
   windows get one at zero specificity (every rule below still wins). */
.lbx_window
  :where(div, span, p, ul, ol, li, table, tbody, tr, td, th, form, input, textarea, select, a, img, b, strong, em) {
  margin: 0;
  padding: 0;
  border: 0;
}

.lbx_overlay,
.lbx_veil {
  position: fixed;
  inset: 0;
  /* every window dims and softly blurs the table behind it, fading in and out (owner 2026-10-17); the blur
     is on the board itself (below), not a backdrop-filter here; 48 % (owner 2026-10-23, was 38 %): the table
     reads as the background at a glance, and the 300 ms fade goes with the blur */
  background: rgb(0 0 0 / 48%);
  z-index: 9991;
  animation: lbx-veil-in 300ms ease-out;
  transition: opacity 300ms ease-in;
}

/* a window that was never modal (Options, Themes, Win, Ranking, ...) gets the same look as a veil the
   pointer passes through, so the top bar and the table keep working as before (the tests and the
   legacy behaviour: the menu, Undo and Login stay clickable while those windows are open) */
.lbx_veil {
  pointer-events: none;
}

.lbx_overlay[data-closing],
.lbx_veil[data-closing] {
  opacity: 0;
}

@keyframes lbx-veil-in {
  from {
    opacity: 0;
  }
}

/* The table's blur under a window (2026-10-23): a backdrop-filter on the full-screen veil was redrawn on every
   frame the window or the cards moved, and the Win window never stops moving (rays, fan, medal), so a phone
   or tablet dropped to ~33 frames a second for as long as it was open (the /my-score/ pages "mulą", owner).
   A filter on the board is redrawn only when the board itself moves: 60 frames again. Only the board: the top
   bar and the menu stay sharp and usable over the non-modal windows, as they were above the veil. */
#main {
  transition: filter 300ms ease-out;
}

body:has(> .lbx_overlay:not([data-closing]), > .lbx_veil:not([data-closing])) #main {
  filter: blur(3px);
}

/* The box: centred by `inset: 0; margin: auto` (the desktop tier below lifts `bottom` by the ad band),
   a flex column, the width clamped to the viewport minus 16 px gutters and the height to the viewport
   minus the band. Contrast tokens for older players (BRIEF item 2b): body #333, captions #555, darker
   marks and dividers, nothing under 13 px. Ratios over #eee: #333 10.9:1, #555 6.5:1. */
.lbx_window {
  position: fixed;
  inset: 0;
  margin: auto;
  --lbx-w: 677px;
  --lbx-h: 422px;
  --text-2: #333;
  --text-3: #555;
  --muted: #555;
  --mark-off: #555;
  --star-off: #8a8a8a;
  --win-hair: rgb(0 0 0 / 16%);
  --win-border: #c2c2c2;
  --fs-label: 13px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  /* --lbx-k: 0.9 on large touch screens (iPad, iPad Pro; owner 2026-10-17: "troche mniejsze okna o 10%"),
     the box only: the content keeps its size, the ad creatives are never scaled */
  width: min(calc(var(--lbx-w) * var(--lbx-k, 1)), 100vw - 32px);
  height: calc(var(--lbx-h) * var(--lbx-k, 1));
  max-width: none;
  /* the desktop tier places the box in [--win-top, vh - --win-bottom] (the top bar above; the footer
     dock and the anchor room below, see tokens.css), so neither the bar, the dock (GAMES-010) nor the
     bottom ad ever lies under or over a window. Not scaled by --lbx-k: the 10 % smaller box must still be
     allowed the whole room when its content needs it (owner 2026-10-18, an iPad window 770 x 840: the Win
     window was held at 90 % of the room and its buttons covered the ad box) */
  max-height: calc(100dvh - var(--win-top) - var(--win-bottom) - 8px);
  background: var(--win-bg);
  color: var(--text);
  /* Helvetica only, like legacy: Arial would supply the Arabic and Hebrew glyphs of the language
     list and change those rows' height (the system fallback font is taller). */
  font-family: var(--win-font);
  font-size: var(--fs-body);
  border: 1px solid rgb(0 0 0 / 12%);
  border-radius: 16px;
  box-shadow: 0 10px 30px -10px rgb(0 0 0 / 50%);
  overflow: hidden;
  z-index: 9995;
  opacity: 1;
  transform: translate(var(--drag-x, 0px), var(--drag-y, 0px)); /* the header drag (WindowFrame) */
  transition: none;
}

.lbx_window a {
  text-decoration: none;
  color: var(--link); /* #18517f, 7.2:1 on #eee (the #3d7aad accent is 3.9:1) */
}

.lbx_window:not(.lbx_info) {
  user-select: none; /* legacy: only the Info window's text is selectable */
}

@media (pointer: coarse) and (min-width: 1024px) and (min-height: 700px) {
  .lbx_window {
    --lbx-k: 0.9;
  }
}

/* ---- window sizes (the legacy fixed sizes; the grown desktop tier in section 3 scales them) ------ */
.lbx_games,
.lbx_multiplayer.mp_games {
  --lbx-w: 960px; /* round 2 (owner): wider than the legacy 830 */
  --lbx-h: 656px;
}
.lbx_games.lbx_ads_big,
.lbx_multiplayer.mp_games.lbx_ads_big {
  --lbx-h: 671px;
}
.lbx_games.no-ads,
.lbx_multiplayer.mp_games.no-ads {
  --lbx-h: 610px;
}
.lbx_multiplayer {
  --lbx-w: 740px;
  --lbx-h: 540px;
}
.lbx_auth_dashboard,
.lbx_ranking {
  --lbx-w: 800px;
  --lbx-h: 600px;
}
.lbx_ranking {
  --lbx-h: 608px;
}
.lbx_auth {
  --lbx-w: 487px;
  --lbx-h: 306px;
}
/* legacy `height: auto` windows: the engine makes them fit-content */
.lbx_pause {
  --lbx-w: 470px; /* 470 = the table's box */
  --lbx-h: 100vh;
}
.lbx_rules,
.lbx_alert,
.lbx_confirm {
  --lbx-w: 640px;
  --lbx-h: 100vh;
}
.lbx_win {
  --lbx-w: 770px;
  --lbx-h: 610px;
}
.lbx_win.no-ads {
  --lbx-h: 420px;
}
.lbx_multiplayer_win {
  --lbx-w: 830px;
  --lbx-h: 525px;
}
.lbx_flags {
  --lbx-w: 691px;
  --lbx-h: 376px;
}

/* legacy phone sheets scrolled inside .lbx_content; the engine hands the scroll to the window's own
   scroller, this is the one declaration of the legacy phone block that still reached the prototype */
@media (max-width: 839px), (max-height: 614px) {
  .lbx_content {
    overflow: auto;
  }
}

/* ---- tabs (the look is in section 3) ---------------------------------------------------------- */
.tab_buttons {
  flex: none;
}

.tab_buttons a {
  cursor: pointer;
  text-align: center;
}

.tab_contents {
  background: var(--win-bg);
}

.lbx_pause .tab_contents {
  padding-bottom: 11px;
}

/* ---- option lists -------------------------------------------------------------------------- */
.option_item {
  display: block;
  width: 100%;
}

.option_item .option_item_entry {
  cursor: pointer;
}

/* ---- Games window -------------------------------------------------------------------------- */
.games_window {
  background: var(--win-bg);
  display: block;
  width: 100%;
}
.option_item .option_item_entry .game_preview {
  vertical-align: middle;
}
.radio .option_item_entry span.stars .caption {
  display: block;
  clear: both;
  text-align: center;
}

/* ---- buttons: the legacy per-window input rules are gone (section 3 styles every input) -------- */
.lbx_buttons a {
  display: inline-block;
}
.stats_cancel_filtr_button {
  text-align: center;
}

/* ---- ads: the empty <ins> keeps its room (--ad-slot-h + the vendor line), the ADVERTISEMENT label --- */
.lbx_multiplayer_win .ads {
  float: left;
  margin: 0 0 0 3px;
  width: 794px;
}
.lbx_window .ads {
  text-align: center;
  position: relative;
  overflow: hidden;
}

.lbx_window .ads::after {
  content: 'ADVERTISEMENT';
  position: absolute;
  top: 0;
  left: 0;
  font-variant: small-caps;
}

.lbx_window.no-ads .ads {
  display: none;
}

/* ---- rules / confirm / alert / what's new ----------------------------------------------------- */
.dashboard_details .rows {
  margin-left: 0;
}
.rules_cont a {
  display: block;
  float: right;
}

/* ---- pause / win tables ------------------------------------------------------------------------ */
.lbx_win .tab_contents {
  justify-content: center;
}
.lbx_win table,
.lbx_pause table,
.lbx_multiplayer_win table {
  margin: auto;
  width: 468px;
  padding: 9px 18px;
}
.lbx_win table,
.lbx_multiplayer_win table {
  padding: 4px 18px 0;
}
.lbx_win td,
.lbx_pause td,
.lbx_multiplayer_win td {
  font-size: 21px;
  color: #222;
  padding: 2px 0;
}
.lbx_win td.name,
.lbx_pause td.name,
.lbx_multiplayer_win td.name {
  text-align: left;
}
.lbx_win td.value,
.lbx_pause td.value,
.lbx_multiplayer_win td.value {
  text-align: right;
}
.lbx_win td.td_bonus,
.lbx_win td.td_player,
.lbx_win td.td_score_type,
.lbx_win td.td_game_type,
.lbx_multiplayer_win td.td_bonus,
.lbx_multiplayer_win td.td_player,
.lbx_multiplayer_win td.td_score_type,
.lbx_multiplayer_win td.td_game_type {
  color: #3d7aad;
}
.lbx_win td.td_player,
.lbx_multiplayer_win td.td_player {
  border-top: 1px dotted #aaa;
  text-align: right;
  font-size: 13px;
  padding-top: 5px;
  padding-bottom: 5px;
  opacity: 1;
}
.lbx_win td.td_player {
  padding-bottom: 10px;
  padding-top: 13px;
}
.lbx_win td.td_score,
.lbx_multiplayer_win td.td_score,
.lbx_pause td.td_content {
  font-size: 45px;
  font-weight: bold;
  padding-bottom: 0;
}
.lbx_pause td.td_content {
  font-size: 40px;
  text-align: center;
}
.lbx_win td.td_best_score,
.lbx_multiplayer_win td.td_best_score {
  padding-top: 0;
}
.win_inactive {
  opacity: 0.1;
  transition: opacity 500ms;
}
.win_active {
  opacity: 1;
}

/* ---- multiplayer result (.lbx_multiplayer_win) ---------------------------------------------- */
.lbx_multiplayer_win .win_cont {
  padding-top: 14px;
  padding-left: 14px;
  display: inline-block;
}
.lbx_multiplayer_win table {
  float: left;
  padding-bottom: 10px;
  margin: 3px 3px 0;
  width: 395px;
  position: relative;
}
.lbx_multiplayer_win .winner {
  background-repeat: no-repeat;
  font-size: 20px;
  margin-top: 0;
  text-align: center;
  width: 796px;
}
.lbx_multiplayer_win .stat_2 {
  padding-bottom: 10px;
}
.lbx_multiplayer_win .stat_2,
.lbx_multiplayer_win .stat_1 {
  height: 241px;
}
.lbx_multiplayer_win .winner td {
  font-size: 27px;
}
.lbx_multiplayer_win td {
  font-size: 16px;
  padding: 5px 0;
}
.lbx_multiplayer_win td.value {
  font-weight: bold;
}
.lbx_multiplayer_win td.td_game_player {
  font-size: 18px;
}
.lbx_multiplayer_win td.td_game_player .player_name {
  font-weight: bold;
  display: block;
  margin-top: 0;
  position: absolute;
  width: 270px;
  left: 106px;
  top: 11px;
  z-index: 10;
}

/* ---- info (contact form) --------------------------------------------------------------------- */
.contact_cont {
  font-weight: bold;
  margin-top: 10px;
}
/* #contact_email keeps its legacy 1 px #ccc border, 2 px padding and 5 px radius: the id outranks the
   skin's input rule (it did in the prototype too). */
.contact_cont textarea,
#contact_email {
  width: 100%;
  height: 200px;
  margin-top: 10px;
  border: 1px solid #ccc;
  padding: 2px;
  border-radius: 5px;
}
#contact_email {
  width: 55%;
  height: 20px;
}
.contact_cont #contact_send,
.contact_cont #contact_back {
  background-color: #3d7aad;
  color: #fff;
  cursor: pointer;
  text-align: center;
  border-radius: 5px;
}
.contact_cont .contact_sent {
  font-size: 16px;
  padding: 15px;
  text-align: center;
}

/* ---- Themes window (main.css .skin_item) ----------------------------------------------------- */
.skin_item {
  height: 170px;
  width: 100%;
  display: block;
  background-position: top left;
  background-color: #fff;
  background-repeat: repeat-x;
  border-bottom: 1px dotted #ccc;
  cursor: pointer;
}
/* The board's `.card` rule (board.css: absolute, sized, z-index, transform) must not reach the tile images. */
.skin_item img.card {
  position: relative;
  width: auto;
  height: auto;
  z-index: auto;
  transform: none;
  transition: none;
  will-change: auto;
}
/* The card and back pictures of the theme tiles and the Advanced tab always take a card's size (109 x 144,
   the size of every 1x file). Their size used to come from the file and its srcset: on the iPad, after a
   few deck changes in the Advanced tab, Safari drew a picture at half size (the x2 density kept for a
   new src, or no size while loading) and the negative margins below slid the whole fan off to the left
   (owner 2026-10-18). */
.skin_item img.card,
.skin_item img.back_card {
  width: 109px;
  height: 144px;
}
.select_theme .card,
.select_theme img.back_card {
  display: inline;
  position: relative;
  left: 180px;
  margin-left: -65px;
  top: 14px;
}
.select_advanced_theme .card,
.select_advanced_theme img.back_card {
  display: inline;
  left: 140px;
  margin-left: -109px;
  position: relative;
  top: 14px;
}
.select_advanced_theme img.back_card {
  left: 514px;
  height: 144px;
  border-radius: 10px;
}
.cards_content {
  display: block;
  height: 162px;
  margin: auto;
  width: 440px;
}
.select_advanced_theme {
  width: 100%;
  /* at least the tab's height; on a low screen the rows scroll and the table background goes on under them (it ended at
     the tab's height, and the last rows stood on white, 2026-11-03) */
  height: auto; /* over .skin_item's 170 px */
  min-height: 100%;
  padding-bottom: 8px;
  box-sizing: border-box;
  float: left;
  display: block;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  border: none;
}
.skin_item div.label_bg {
  color: #fff;
  display: block;
  margin-bottom: 0;
  position: relative;
}
.skin_item div.label_bg .skin_item_default {
  float: right;
  text-align: center;
}
#mobile .skin_item_default {
  display: none;
} /* legacy navi_mobile.css: no "restore theme" link on phones */
/* The Advanced tab (owner 2026-10-31, redesigned for the Mahjong row): the fan of cards with its two pairs of arrows
   (the deck under the cards, the back under the back), then the row of Mahjong tiles with its own pair; the rows a
   step apart, centred, within the 950 x 600 window. */
.theme_board_top {
  display: block;
  padding-top: 8px;
  width: 100%;
}
.theme_board_bottom {
  text-align: center;
  display: block;
  width: 100%;
  margin-top: 6px;
}
.theme_board_bottom_left {
  display: inline-block;
  height: 44px;
  margin: auto;
  padding-left: 60px;
  text-align: center;
  width: 290px;
}
.theme_board_bottom_right {
  display: inline-block;
  height: 44px;
  margin: auto;
  padding-left: 82px;
  text-align: center;
  width: 230px;
}
.theme_board_tiles {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
}
.theme_board_tiles .theme_item_navi {
  float: none;
}
.tiles_content {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: 0 10px;
}
.tiles_content img.tile {
  width: 60px;
  height: 77px;
}
.tiles_content img.tile_back {
  margin-left: 14px;
}
/* the Advanced tab's row of Kulki's balls (owner 2026-11-03), its arrows either side */
.theme_board_balls {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
}
.theme_board_balls .theme_item_navi {
  float: none;
}
.balls_content {
  display: flex;
  gap: 6px;
  margin: 0 10px;
}
.balls_content img.ball {
  width: 40px;
  height: 40px;
}
.theme_item_navi {
  float: left;
  font-family: Helvetica;
  font-weight: bold;
  text-align: center;
}
.no_more_item {
  color: #222;
}

/* ---- Auth (login / register) ----------------------------------------------------------------- */
.lbx_auth[data-mode='register'] .lbx_header {
  background: var(--header-reg); /* legacy auth.js:380 */
}
.auth_cont {
  font-size: 15px;
}
/* Legacy's `#CCC` placeholder rule shared a selector list with `:-moz-placeholder`, which Chrome
   rejects whole, so placeholders kept the browser's default grey; no rule here on purpose. */
.auth_register .row,
.auth_login .row,
.auth_info .row,
.dashboard_cont .row {
  margin: 5px;
  text-align: center;
}
.auth_register .row span,
.auth_login .row span {
  display: inline-block;
  text-align: left;
}
.register_desc,
.login_desc,
.info_desc {
  margin-bottom: 25px;
  text-align: left;
}
.info_desc {
  margin-bottom: 15px;
}
.register_desc.form_error,
.login_desc.form_error {
  color: red;
  margin-top: 15px;
  text-align: center;
}
.auth_valid_info {
  text-align: right;
}
.auth_info .welcome_player,
.dashboard_cont .welcome_player {
  font-size: 28px;
  margin-top: 18px;
  margin-bottom: 21px;
  white-space: nowrap;
}

/* ---- Dashboard / Ranking / Flags --------------------------------------------------------------- */
.dashboard_cont .tab_contents {
  position: relative;
}
.dashboard_main,
.dashboard_details,
.stats_info,
.panel_logout {
  float: left;
}
.dashboard_main {
  width: 60%;
}
.dashboard_details,
.stats_info,
.panel_logout {
  width: 40%;
}
.dashboard_cont .edit-country {
  color: var(--link);
}
.dashboard_cont .edit-country:hover {
  color: #000;
}
#most_played_chars.no_chart_data {
  font-size: 14px;
  font-style: italic;
  padding-bottom: 25px;
  padding-top: 25px;
}
.stats_info,
.panel_logout {
  text-align: center;
  font-size: 13px;
  color: #222;
}
.panel_logout {
  bottom: 14px;
  position: absolute;
  right: 0;
  text-align: right;
}
.panel_logout a {
  padding-right: 20px;
}
.dashboard_details .row {
  text-align: left;
}
.dashboard_details ul {
  list-style-position: inside;
}
.dashboard_details ul li {
  padding: 10px 0;
  display: block;
} /* legacy showed the rows with display: block: no bullets */
.most_played_label {
  font-size: 24px;
  margin: 5px 0 0;
}
.medium_label {
  font-size: 24px;
  text-align: center;
  margin: 5px 0 0;
}

.stats_filter,
.stats_results {
  float: left;
}
.stats_filter {
  background-color: var(--win-bg);
  left: -345px;
  position: absolute;
  width: 335px;
  z-index: 1;
}
.stats_results {
  width: 100%;
}
.stats_results .rows {
  text-align: center;
}
.stats_results .filter {
  cursor: pointer;
  margin: 8px 8px 0;
}
.stats_results .legend {
  background: transparent;
  border: none;
  margin: 3px 8px 0;
  text-align: left;
}
.stats_results .filter span {
  margin: 0 5px;
  display: inline-block;
  color: #fff;
}
.stats_results .filter span.change_filtr {
  position: absolute;
  right: 2px;
  bottom: 6px;
}
.stats_table_cont {
  border-bottom: 1px solid #ccc;
}
.stats_results table thead {
  background-color: #ccc;
  font-weight: bold;
}
.stats_results table {
  font-size: 13px;
  background-color: #ddd;
  border: 1px solid #ccc;
  margin: 20px;
  width: 94%;
  border-collapse: collapse;
}
.stats_results table a {
  color: #18517f;
}
.stats_results table a:hover {
  text-decoration: underline;
}
/* Mahjong: the deal's shape under the game's name (owner 2026-10-27) */
.stats_results .game_layout {
  display: block;
  font-size: 11px;
  color: #555;
}
.stats_results tr.odd td {
  background: #eee;
}
.stats_results tr td img {
  vertical-align: middle;
  display: inline-block;
  padding-bottom: 3px;
  padding-left: 1px;
  padding-right: 1px;
}
.stats_results tr:hover,
.stats_results tr:hover td {
  background: #fff;
}
.stats_results td {
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  padding: 4px 10px;
  text-align: center;
}
.stats_results th {
  font-weight: normal;
  color: #fff;
  text-align: left;
  border-right: 1px solid #2c6594;
  border-left: 1px solid #2c6594;
  border-top: 1px solid #2c6594;
  border-bottom: none;
  padding: 4px 15px;
}
.stats_results thead th {
  background: #3d7aad;
  text-align: center;
  color: #fff;
}
.stats_results thead th.thin {
  padding-left: 5px;
  padding-right: 5px;
}
.stats_results .row {
  margin: 0;
}
.stats_filter select {
  font-family: Helvetica;
  font-size: 16px;
  height: 40px;
  padding-left: 10px;
  width: 100%;
}

.lbx_flags .radio .option_item_entry {
  width: 30px;
  float: left;
}
.lbx_flags .option_item::after {
  content: '';
  display: block;
  clear: both;
}

/* ---- Multiplayer rooms window --------------------------------------------------------------- */
.lbx_multiplayer .multi_loading {
  width: 260px;
  display: block;
  margin: 190px auto auto;
  text-align: center;
  font-size: 19px;
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 20px;
  background-color: #fff;
}
.game_countdown {
  background-color: #eee;
  height: 382px;
  opacity: 0.85;
  position: absolute;
  top: 80px;
  width: 100%;
}
.game_countdown .wrapper {
  padding: 40px 30px 30px;
}
.game_countdown .c_title {
  font-size: 28px;
  font-weight: bold;
  padding-bottom: 30px;
  text-align: center;
}
.multi_wrapper {
  width: 91%;
  position: relative;
}
.multi_wrapper div {
  display: inline-block;
}
.multi_room_label,
.multi_game_label {
  width: 50%;
}
div.multi_game_label {
  display: block;
  float: right;
  text-align: right;
  font-weight: bold;
}
.multi_l,
.multi_r,
.multi_vs {
  line-height: 1.7;
  text-align: center;
}
.multi_l {
  width: 40%;
}
.multi_r {
  width: 40%;
  margin-left: 115px;
}
.multi_vs {
  font-size: 25px;
  vertical-align: super;
  display: block;
  line-height: 1;
  width: 15%;
  margin-left: 13px;
  position: absolute;
}
.multi_vs .game_time {
  display: block;
  margin-top: 3px;
}
div.multi_player_label {
  display: block;
}
div.multi_player_name {
  color: #2c6594;
  display: block;
  font-size: 18px;
  font-weight: bold;
  overflow: hidden;
  line-height: 1.2;
}
.multi_player_name .multi_country {
  color: #555;
  font-weight: lighter;
  font-size: 15px;
}
.multi_player_name .multi_country img {
  vertical-align: middle;
}
.multi_player_name.empty {
  font-weight: normal;
}
#footer_multiplayer.bigger {
  font-size: 15px;
}

/* ==== 2. the engine (prototype shared/engine.css, window parts) ================================== */

/* Content-sized windows (legacy `height: auto`): fit-content, clamped by the box's max-height. */
.lbx_pause,
.lbx_rules,
.lbx_alert,
.lbx_confirm,
.lbx_no_moves {
  height: fit-content;
}

.lbx_pause {
  width: fit-content;
  max-width: calc(100vw - 32px);
}

/* The Auth box was 306 px tall with a 342 px body overflowing it (legacy quirk): it now grows to its
   form (fit-content, the legacy size as the minimum) and scrolls only when the viewport is shorter. */
.lbx_auth {
  height: fit-content;
  min-height: min(var(--lbx-h), 100dvh - var(--win-top) - var(--win-bottom) - 8px);
}

.lbx_container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  left: 0;
  top: 0;
  width: auto;
  /* painted, not transparent: the entrance animation composites the container as its own layer
     and a transparent one shows the layer behind for one device pixel under the header on
     2.625 dpr phones (seen on Pixel 7 with the menu panel still open under a sheet) */
  background: var(--win-bg);
}

.lbx_header {
  flex: none;
  box-sizing: border-box;
  position: relative;
  min-height: var(--win-header-h);
  height: auto;
  padding: 0 56px 0 var(--gutter);
  display: flex;
  align-items: center;
  background: var(--header); /* #91b57b exact (tests read it) */
  border-bottom: 1px solid rgb(0 0 0 / 10%);
  border-radius: 0;
  box-shadow: none;
  color: #fff;
  font-size: var(--fs-header);
  font-weight: 700;
  line-height: 1.2;
  cursor: move; /* WIN-010 */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* The content box is not the scroller: it hands the height down to the window's own scroller. */
.lbx_content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: transparent;
}

/* ---- close / minimize: round hit areas inside the header ----------------------------------- */
.lbx_close,
.lbx_minimize {
  position: absolute;
  top: calc((var(--win-header-h) - 36px) / 2);
  right: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: transparent;
  box-shadow: none;
  color: #fff;
  z-index: 2;
  transition: background-color var(--t-fast);
}

.lbx_minimize {
  right: 44px;
}

.lbx_close::before,
.lbx_minimize::before {
  content: '';
  width: var(--icon-l);
  height: var(--icon-l);
  background: currentColor;
  mask: var(--i-x) center / contain no-repeat;
}

.lbx_minimize::before {
  mask-image: var(--i-minus);
}

.lbx_close:hover,
.lbx_minimize:hover {
  background: rgb(0 0 0 / 15%);
}

.lbx_close:active,
.lbx_minimize:active {
  background: rgb(0 0 0 / 25%);
}

/* ---- the scroll chains, window by window ---------------------------------------------------- */
/* Wrappers between .lbx_content and the scroller: flex columns that pass the height down. */
.games_wrapper,
.options_window,
.skin_content,
.info_content,
.dashboard_cont,
.lbx_multiplayer .options_window > div {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  box-shadow: none;
}

/* The scrollers the tests write scrollTop to (visual-helpers scrollToBottom / scrollToPage). The
   `Tabs` component sets display: block | none inline on .tab_contents: a flex item, never a container. */
.games_window,
.tab_contents,
.lbx_auth_dashboard .tab_contents,
.lbx_ranking .tab_contents,
.lbx_multiplayer .tab_contents,
.lbx_win .tab_contents {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  box-shadow: none;
  position: relative;
}

.lbx_win.no-ads .tab_contents {
  height: auto;
}

/* The Win window laid its table out with display: grid (no inline style): a flex column instead,
   so the button row can sit at the bottom and stick. */
.lbx_win .tab_contents {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lbx_win .win_cont {
  flex: none;
}

/* The short dialogs (static markup, no inline display on .tab_contents): a flex column, so the
   button row can sit at the bottom of a phone sheet (margin-top: auto; no effect in a fit-content card). */
.lbx_rules .tab_contents,
.lbx_confirm .tab_contents,
.lbx_alert .tab_contents,
.lbx_pause .tab_contents {
  display: flex;
  flex-direction: column;
}

.rules_cont,
.pause_cont {
  flex: none;
}

.lbx_rules .rules_buttons,
.lbx_confirm .rules_buttons,
.lbx_alert .rules_buttons,
.lbx_pause .win_buttons {
  margin-top: auto;
}

/* Multiplayer: the rooms list was a 382 px scroller inside a 460 px tab with the button pulled up
   over it (top: -77px); now the tab scrolls and the button row is a sibling below. */
.lbx_multiplayer .option_item {
  height: auto;
  overflow: visible;
}

.lbx_multiplayer .button_wrapper {
  flex: none;
  position: static;
  top: 0;
}

.lbx_multiplayer .multi_loading {
  margin: auto;
}

/* Dashboard / Ranking: the stats table scrolls under the filter chips; the filter panel spans the tab. */
.dashboard_cont .tab_contents {
  overflow: auto;
}

.stats_results {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  float: none;
  box-sizing: border-box;
}

.stats_table_cont,
.lbx_ranking .stats_table_cont,
.lbx_ranking .stats_table_cont.global_stats_ads {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  overflow: auto;
  overscroll-behavior: contain;
}

.stats_results .rows {
  flex: none;
}

.stats_filter,
.lbx_ranking .stats_filter {
  top: 0;
  bottom: 0;
  height: auto;
  overflow: auto;
  box-sizing: border-box;
}

/* ---- action rows: always visible ----------------------------------------------------------- */
/* Siblings of the scroller where the markup allows (Games, Multiplayer) ... */
.game_button_cont,
.lbx_multiplayer .button_wrapper {
  flex: none;
}

/* ... and sticky at the bottom of the scroller where the buttons live inside it (Win, Pause, Rules,
   Confirm, Alert, What's new, Auth, the stats filter). */
.win_buttons,
.rules_buttons,
.lbx_buttons,
.stats_ok_filtr_button {
  position: sticky;
  bottom: 0;
  z-index: 1;
  flex: none;
  align-self: stretch;
  box-sizing: border-box;
  margin-bottom: 0;
  background: var(--win-bg);
}

.lbx_win .win_buttons,
.lbx_multiplayer_win .win_buttons {
  margin-top: auto;
  padding-bottom: 12px;
}

/* ---- ads: the empty <ins> keeps the room of the tallest allowed creative ----------------------- */
.lbx_window .ads {
  flex: none;
  box-sizing: border-box;
  min-height: calc(var(--ad-slot-h, var(--ad-min-h)) + var(--ad-box-extra));
}

/* the Win window's top box (data-slot 2 too, big screens only; WinWindow renders it before .win_cont):
   a plain block under the header, not the sticky bottom box the skin makes of `.lbx_win .ads` */
.lbx_window .tab_contents > .ads_top {
  position: static;
  margin: 0 0 12px;
  padding-top: 0;
  border-top: 0;
  align-self: stretch;
}

/* ---- entrance on the container, never the box; close = fade, element gone at WINDOW_CLOSE_MS ---- */
@keyframes win-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.lbx_window.lbx_showed .lbx_container {
  animation: win-in var(--t-open) var(--ease-out) both;
}

.lbx_window:not(.lbx_showed) {
  opacity: 0;
  transition: opacity var(--t-close) ease-out;
}

.lbx_window[data-dragging] {
  transition: none; /* the header drag must follow the pointer at once */
}

/* Desktop tier: centre the box in the area above the footer dock and the bottom anchor ad
   (--win-bottom: the measured anchor, or its default room before it shows, so the ad can never
   cover Play / OK). Scoped to the desktop tier: the sheet tier sets `height`, so top + height +
   bottom would be over-constrained and shift the sheet up. */
@media (min-width: 840px) and (min-height: 615px) {
  .lbx_window {
    top: var(--win-top);
    bottom: var(--win-bottom);
  }
}

/* ---- small windows: the box is the viewport, the anchor is reserved inside -------------------------
   Every window is full screen below 840 px of width or 615 px of height, at any width (owner 2026-10-18,
   an iPad Safari window 1160 x 375: the Games window was a strip with one row; was below 560 px, or below
   501 px high when also below 1000 px wide). 615: below it the desktop Win box (with the top bar and the
   anchor room around it) could not show its ad box above the buttons, the sheet gains that room (ad audit
   2026-10-18). The desktop box tier is the rest: 840+ wide and 615+ high. */
@media (max-width: 839px), (max-height: 614px) {
  .lbx_window,
  .lbx_pause,
  .lbx_rules,
  .lbx_alert,
  .lbx_confirm,
  .lbx_auth {
    width: 100vw;
    max-width: none;
    height: min(100dvh, var(--vvh, 100dvh)); /* --vvh: the visual viewport above the keyboard (ui/vvh.ts) */
    min-height: 0;
    max-height: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  /* the anchor never covers the action row or the in-window ad, but a full-screen window keeps only the
     room the anchor really takes (owner 2026-10-17: "jak okno jest na cały ekran ... niech ads będzie na
     dole a reszta miejsca na treść ... dotyczy to wszystkich okien"): the measured height, or the one this
     browser remembered (AdAnchorBand), 0 while no anchor shows; not the desktop's 110 px default, which
     left an empty strip under the window's own ad. The content scrollers take the rest, so the in-window
     ad sits at the bottom. */
  .lbx_container {
    padding-bottom: calc(var(--ad-anchor-h) + env(safe-area-inset-bottom, 0px));
  }

  .lbx_header {
    padding-top: env(safe-area-inset-top, 0px);
    min-height: calc(var(--win-header-h) + env(safe-area-inset-top, 0px));
  }

  .lbx_close,
  .lbx_minimize {
    top: calc((var(--win-header-h) - 36px) / 2 + env(safe-area-inset-top, 0px));
  }

  .tab_contents,
  .games_window {
    scroll-padding-bottom: 96px;
  }

  .lbx_window input[type='text'],
  .lbx_window input[type='email'],
  .lbx_window input[type='password'],
  .lbx_window textarea {
    scroll-margin: 16px;
  }
}

/* ---- focus rings ----------------------------------------------------------------------------- */
.lbx_window a:focus-visible,
.lbx_window input:focus-visible,
.lbx_window select:focus-visible,
.lbx_window textarea:focus-visible {
  outline: var(--focus-light);
  outline-offset: -2px;
}

.lbx_close:focus-visible,
.lbx_minimize:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -2px;
}

/* ==== 3. the skin (prototype mix/windows.css) ==================================================== */
/* Pocket's windows with three changes (BRIEF items 2 / 3 / 6): the ad boxes in Games and Win stay
   visible above the action row, higher contrast for older players, and every window is a full sheet
   on phones. Look: radius 16, hairline edge, one directional shadow, 48 px #91b57b header (exact; 56 px
   on the grown desktop tier), a plain white X in a 40 px hit box, flat tabs on the blue bar, 48 / 54 px
   option rows with SVG marks and a blue-grey tint + blue bar on the selected row (BRIEF item 9: #c9d4df
   is 1.30:1 against the #eee rows, body #111 on it 12.6:1), 44 px blue buttons. */

.lbx_multiplayer {
  background: var(--win-bg);
}

.lbx_window a.theme_item_navi {
  color: #fff;
}

/* ---- header: 48 px, left-aligned title, 40 px round close inside ------------------------------ */
.lbx_header {
  padding: 0 56px 0 16px;
  justify-content: flex-start;
  text-align: left;
  font-size: var(--fs-header);
  font-weight: 600;
}

/* The close X (BRIEF item 9): calm but clearly visible: no plate at rest, a faint one (12 % black) on
   hover and 20 % while pressed. The element stays a.lbx_close[data-action=close] in the same place with
   its 40 px hit box. The glyph: the --i-x mask scales its stroke with the drawing (a 22 px X would carry
   a 3.9 px stroke), so the close draws two 30 x 3 px white bars on its ::before / ::after instead (mask
   off), rotated 45 deg: a 23 px glyph with a 3 px round stroke. The minimize (Multiplayer only) keeps
   the kit's minus at a 24 px box / 200 % (20 px drawn). */
.lbx_window .lbx_close,
.lbx_window .lbx_minimize {
  top: calc((var(--win-header-h) - 40px) / 2);
  right: 4px;
  width: 40px;
  height: 40px;
  /* a rounded square, not a circle: the browser hit-tests the rounding, and a click in the corners of the
     40 px box went to the header (it starts a window drag) and the window stayed open (owner 2026-10-23) */
  border-radius: 10px;
  background: transparent;
  color: #fff;
  z-index: 3;
}

.lbx_window .lbx_close::before,
.lbx_window .lbx_close::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 30px;
  height: 3px;
  margin: -1.5px 0 0 -15px;
  background: #fff;
  border-radius: 1.5px;
  mask: none;
  transform: rotate(45deg);
}

.lbx_window .lbx_close::after {
  transform: rotate(-45deg);
}

.lbx_window .lbx_minimize::before {
  width: 24px;
  height: 24px;
  mask-size: 200%;
}

.lbx_window .lbx_close:hover,
.lbx_window .lbx_minimize:hover {
  background: rgb(0 0 0 / 12%);
}

.lbx_window .lbx_close:active,
.lbx_window .lbx_minimize:active {
  background: rgb(0 0 0 / 20%);
}

.lbx_window .lbx_minimize {
  right: 48px; /* 4 px between the two plates */
}

.lbx_multiplayer .lbx_header {
  padding-right: 96px;
}

/* ---- tabs: flat segmented strip on the blue bar ------------------------------------------------ */
.lbx_window .tab_buttons {
  display: flex;
  gap: 4px;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 8px 12px 0;
  overflow: visible;
  background: var(--tabbar);
  box-shadow: none;
}

.lbx_window .tab_buttons a {
  flex: 1 1 0;
  float: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: auto;
  min-width: 0;
  height: 40px;
  margin: 0;
  padding: 0 10px;
  background: rgb(255 255 255 / 8%); /* white on the blend ~#3d719d: 5.2:1 (18 % gave 4.1:1) */
  color: #fff;
  font-size: var(--fs-tab);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 10px 10px 0 0;
  box-shadow: none;
  transition: background-color var(--t-fast);
}

.lbx_window .tab_buttons a:hover {
  background: rgb(255 255 255 / 24%);
}

.lbx_window .tab_buttons a.tab_selected {
  background: var(--win-bg);
  color: var(--text);
  font-weight: 600;
}

/* ---- option rows: 48 px, SVG marks on ::after, a solid accent bar on the selected row ----------- */
.option_item {
  border-bottom: 0;
}

.option_item .option_item_entry {
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: var(--win-row-h);
  padding: 8px 56px 8px 16px;
  border-bottom: 1px solid var(--win-hair);
  background: none;
  box-shadow: none;
  color: var(--text);
  font-size: var(--fs-body);
  line-height: 1.3;
  transition: background-color var(--t-fast);
}

.option_item .option_item_entry:hover {
  background: rgb(0 0 0 / 4%);
}

/* the chosen row: a warm grey pressed into the list, no bar (owner 2026-10-27: "bardziej szary, czyli ciepły ... lekko
   wklęsły, jak w starym pasjansie"; it was a blue-grey with a blue bar) */
.radio .option_item_entry.selected,
.tab_contents .selected {
  background: var(--win-row-sel);
  box-shadow: var(--win-row-press);
  font-weight: 700;
}

/* a checked checkbox is a state, not a choice among rows: no bar and no bold, only a light tint, half way to the
   chosen radio row's (owner 2026-10-27: "żeby było wiadomo, że jest załączony"; the full tint on four or five rows
   at once read as noise, 2026-10-17) */
.checkbox .option_item_entry.selected {
  background: var(--win-row-ticked);
  box-shadow: var(--win-row-ticked-press);
  font-weight: 400;
}

/* the greys on the blue-grey tint: captions #444 (6.5:1), the off star #5f5f5f (4.2:1); the marks
   below take --accent-lo (4.1:1 on the tint) */
.option_item_entry.selected {
  --text-3: #444;
  --muted: #444;
  --star-off: #5f5f5f;
}

.radio .option_item_entry:not(.disable_bg)::after,
.checkbox .option_item_entry::after {
  content: '';
  position: absolute;
  right: 16px;
  top: 50%;
  width: var(--icon-xl);
  height: var(--icon-xl);
  margin-top: calc(var(--icon-xl) / -2);
  background: var(--text-3);
  mask: var(--i-radio-off) center / contain no-repeat;
}

.checkbox .option_item_entry::after {
  mask-image: var(--i-check-off);
}

.radio .option_item_entry.selected::after {
  background: var(--accent-lo);
  mask-image: var(--i-radio-on);
}

.checkbox .option_item_entry.selected::after {
  background: var(--accent-lo);
  mask-image: var(--i-check-on);
}

/* Options "Old version of the game" (OptionsWindow.tsx LegacySwitch): a link that leaves the page, so an arrow on
   the right where the other rows have their mark */
.option_item.link .option_item_entry::after {
  content: '';
  position: absolute;
  right: 18px;
  top: 50%;
  width: 28px;
  height: 28px;
  margin-top: -14px;
  background: #6f665c;
  mask: var(--i-chevron-down) center / contain no-repeat;
  transform: rotate(-90deg);
}

/* a setting this device cannot change (the classic mode on an old browser): greyed out, the line under it says why */
.option_item .option_item_entry.locked {
  cursor: default;
  color: var(--muted);
}

.option_item .option_item_entry.locked:hover {
  background: none;
}

.checkbox .option_item_entry.locked::after {
  background: var(--muted);
  opacity: 0.6;
}

.option_item_note {
  display: block;
  margin-top: 2px;
  font-size: var(--fs-label);
}

.option_item .header_label {
  display: block;
  padding: 14px 16px 4px;
  color: var(--text-3);
  font-size: var(--fs-label);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Flags: a grid of 44 px cells */
.lbx_flags .option_item {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  box-sizing: border-box; /* the legacy 100 % width + the padding overflowed the scroller sideways */
  padding: 8px;
}

.lbx_flags .radio .option_item_entry {
  float: none;
  justify-content: center;
  width: 44px;
  min-height: 44px;
  padding: 0;
  border: 0;
  border-radius: 8px;
}

/* the selection ring takes the selection bar's blue, like the rows */
.lbx_flags .radio .option_item_entry.selected {
  box-shadow: inset 0 0 0 3px var(--win-row-bar);
}

/* ---- Games: the sort row above the list (GamesWindow.tsx, owner 2026-10-23); it does not scroll ------ */
.games_sort {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--win-panel);
  border-bottom: 1px solid var(--win-border);
  color: var(--text-2);
  font-size: 14px;
}

.games_sort_label {
  margin-right: 4px;
}

.games_sort_btn {
  height: 30px;
  padding: 0 14px;
  border: 1px solid var(--win-border);
  border-radius: 15px;
  background: #fff;
  color: var(--text-2);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.games_sort_btn:hover {
  border-color: var(--accent);
}

.games_sort_btn.on {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}

@media (pointer: coarse) {
  .games_sort_btn {
    height: 36px;
    border-radius: 18px;
  }
}

/* ---- Games: 88 px rows (preview 120), SVG stars over the hidden PNGs ------------------------ */
.games_window {
  border-bottom: 0;
}

.games_window .option_item .option_item_entry {
  display: block;
  height: auto;
  min-height: 88px;
  padding: 12px 56px 12px 16px;
}

.option_item .option_item_entry .game_preview {
  float: left;
  width: 120px;
  height: auto;
  margin: 0 14px 0 0;
  border-radius: 6px;
}

.games_window .game_name {
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
}

/* "New" next to the name of a newly added game (GamesWindow.tsx NEW_GAMES): a small green pill like the
   window header's green, white bold capitals (5.6:1) */
.games_window .game_new {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 7px;
  border-radius: 9px;
  background: #4d7a39;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  line-height: 1.4;
  text-transform: uppercase;
  vertical-align: 2px;
}

.games_window .selected .game_name {
  font-weight: 700;
}

/* too little room for the list beside the ad (AdSlot.tsx useAdRoom, data-ad-tight): the window gives up its extras
   first, before the ad would go (owner 2026-10-26, iPhone held sideways: no game could be chosen): no sort row,
   one-line rows (a small picture and the name; the selected game's radio stays), a slimmer Play row */
.lbx_window[data-ad-tight] .games_sort {
  display: none;
}
.lbx_window.lbx_games[data-ad-tight] .games_window .option_item .option_item_entry {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 0;
  padding: 6px 52px 6px 12px;
}
.lbx_window.lbx_games[data-ad-tight] .option_item .option_item_entry .game_preview {
  float: none;
  flex: none;
  width: 64px;
  margin: 0;
  border-radius: 4px;
}
.lbx_window.lbx_games[data-ad-tight] .games_window :is(.stars, .game_desc) {
  display: none;
}
.lbx_window.lbx_games[data-ad-tight] .games_window .game_name {
  grid-column: auto;
  grid-row: auto;
}
.lbx_window.lbx_games[data-ad-tight] .game_button_cont {
  padding-top: 6px;
  padding-bottom: 6px;
}
.lbx_window.lbx_games[data-ad-tight] .game_button_cont input {
  height: 40px;
}

/* the heading over the card games that are not solitaires (GamesWindow.tsx OTHER_GAMES, owner 2026-10-26): a thin
   line either side of a short caption, like a group title */
.games_window .games_group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 16px 6px;
  color: var(--text-2);
  font-size: 14px;
  font-weight: 700;
}

.games_window .games_group::before,
.games_window .games_group::after {
  content: '';
  flex: 1;
  height: 1px;
  background: currentColor;
  opacity: 0.35;
}

.radio .option_item_entry .game_desc {
  width: auto;
  margin-top: 3px;
  color: var(--text-2);
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
}

.radio .option_item_entry span.stars {
  float: right;
  margin: 2px 0 0 12px;
  line-height: 1;
}

.radio .option_item_entry span.stars .caption {
  margin-top: 3px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 400;
}

/* The star PNGs stay in the DOM (src pinned); their pixels are pushed out of the box with
   object-position and the box paints a masked SVG star instead. */
.radio .option_item_entry span.stars img {
  width: 16px;
  height: 16px;
  margin-left: 3px;
  object-fit: none;
  object-position: 100px 100px;
  background: var(--star);
  mask: var(--i-star) center / contain no-repeat;
}

.radio .option_item_entry span.stars img[src$='star_gray.png'] {
  background: var(--star-off);
  mask-image: var(--i-star-outline);
}

/* ---- buttons: 44 px, radius 10, 16 px / 600 -------------------------------------------------- */
.lbx_window input[type='button'],
.lbx_window input[type='submit'] {
  appearance: none;
  display: inline-block;
  box-sizing: border-box;
  height: var(--btn-h);
  min-width: 160px;
  width: auto;
  margin: 0;
  padding: 0 24px;
  background: var(--accent);
  color: #fff;
  font-family: var(--win-font);
  font-size: 16px;
  font-weight: 600;
  line-height: var(--btn-h);
  text-align: center;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color var(--t-fast);
}

.lbx_window input[type='button']:hover,
.lbx_window input[type='submit']:hover {
  background: var(--accent-hi);
}

.lbx_window input[type='button']:active,
.lbx_window input[type='submit']:active {
  background: var(--accent-lo);
}

.lbx_window input[type='button']:disabled,
.lbx_window input[type='button'].disabled,
.lbx_multiplayer .button_wrapper input.disabled {
  background: var(--disabled);
  cursor: default;
}

.lbx_games .game_button_cont input,
.lbx_multiplayer.mp_games .game_button_cont input {
  min-width: 200px;
  font-size: 18px;
}

/* Play is the window's one big action (owner 2026-10-17: "trochę większy") */
.lbx_window.lbx_games .game_button_cont input,
.lbx_window.lbx_multiplayer.mp_games .game_button_cont input {
  height: 52px;
  line-height: 52px;
  border-radius: 10px;
}

/* secondary: the first (Cancel) button of a Confirm pair and the Win window's Redeal */
.lbx_confirm .rules_buttons input:first-child,
.lbx_win .win_buttons input.redeal_game {
  background: #fff;
  color: var(--accent-lo);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.lbx_confirm .rules_buttons input:first-child:hover,
.lbx_win .win_buttons input.redeal_game:hover {
  background: #f3f7fa;
}

/* ---- action rows: one bar, buttons centred, pinned by the engine ------------------------------- */
.game_button_cont,
.rules_buttons,
.win_buttons,
.lbx_buttons,
.stats_ok_filtr_button,
.lbx_multiplayer .button_wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: auto;
  margin: 0;
  padding: 12px 16px;
  border-top: 1px solid var(--win-hair);
  background: var(--win-bg);
  text-align: center;
}

.lbx_win .win_buttons,
.lbx_multiplayer_win .win_buttons {
  padding-bottom: 12px;
}

.lbx_confirm .rules_buttons input {
  flex: 1 1 0;
  min-width: 0;
}

.lbx_buttons a {
  margin: 0;
}

.lbx_buttons .or {
  color: var(--muted);
}

/* ---- text boxes --------------------------------------------------------------------------------- */
.rules_cont,
.lbx_cont_row .rows,
.dashboard_cont .rows {
  margin: 16px;
  padding: 12px 14px;
  background: var(--win-panel);
  border: 1px solid var(--win-border);
  border-radius: 10px;
  color: var(--text-2);
  font-size: var(--fs-body);
  line-height: 1.45;
}

.lbx_confirm .rules_cont,
.lbx_alert .rules_cont {
  margin: 20px 20px 8px;
  padding: 0;
  background: none;
  border: 0;
  color: var(--text);
  line-height: 1.5;
}

.rules_cont a {
  margin-top: 8px;
}

/* ---- forms: 44 px / 16 px inputs, radius 8 ---------------------------------------------------- */
.lbx_window input[type='text'],
.lbx_window input[type='email'],
.lbx_window input[type='password'],
.lbx_window textarea,
.lbx_window select {
  box-sizing: border-box;
  height: var(--btn-h);
  padding: 0 12px;
  background: #fff;
  border: 1px solid var(--win-border);
  border-radius: 8px;
  color: var(--text);
  font-family: var(--win-font);
  font-size: 16px;
  transition: border-color var(--t-fast);
}

.lbx_window textarea {
  height: 160px;
  padding: 10px 12px;
  resize: vertical;
}

.lbx_window input[type='text']:focus,
.lbx_window input[type='email']:focus,
.lbx_window input[type='password']:focus,
.lbx_window textarea:focus,
.lbx_window input[type='text']:focus-visible,
.lbx_window input[type='email']:focus-visible,
.lbx_window input[type='password']:focus-visible,
.lbx_window textarea:focus-visible {
  border-color: var(--accent);
  box-shadow: inset 0 0 0 1px var(--accent); /* a 2 px flat ring, no blur */
  outline: none;
}

.auth_register .row,
.auth_login .row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
}

.auth_register .row span,
.auth_login .row span {
  flex: 0 0 35%;
  width: auto;
}

.auth_register .row input,
.auth_login .row input {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
}

.register_desc,
.login_desc {
  margin-bottom: 14px;
}

.lbx_auth .lbx_cont_row .rows {
  margin-bottom: 0;
  border-bottom-left-radius: 10px;
}

/* Login / Register: no rule over the buttons (owner 2026-10-17: the window-wide hairline right under the
   form's box did not fit); the form box above already closes the content */
.lbx_auth .lbx_buttons {
  border-top: 0;
}

.auth_valid_info {
  margin-top: 4px;
  color: var(--muted);
  font-size: 13px;
}

/* Info (contact form) */
.contact_cont {
  padding: 16px;
  color: var(--text-2);
  font-size: var(--fs-body);
}

.contact_form {
  height: auto;
  overflow: hidden;
}

.contact_cont textarea,
.contact_cont #contact_email {
  margin-top: 10px;
}

.contact_cont #contact_email {
  width: 55%;
  height: var(--btn-h);
}

.contact_cont #contact_send,
.contact_cont #contact_back {
  display: inline-block;
  float: right;
  width: auto;
  min-width: 140px;
  height: var(--btn-h);
  margin-top: 10px;
  padding: 0 24px;
  font-size: 16px;
}

/* the message is under the server's 40-byte minimum (SimpleWindows.tsx tooShortMessage): a note under the box,
   the window's warm red at 5.3:1 on the panel */
.contact_cont .contact_short {
  margin: 6px 0 0;
  color: #a8322a;
  font-size: 14px;
  font-weight: 600;
}

/* "attach a picture of the board and technical details" (owner 2026-10-25, ui/errorReport.ts): a tick, the words and
   a small picture of the board that fades when unticked */
.contact_cont .contact_attach {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  color: var(--text-2);
  font-size: 15px;
  line-height: 1.3;
  cursor: pointer;
}

.contact_cont .contact_attach input {
  flex: none;
  width: 20px;
  height: 20px;
  margin: 0;
  accent-color: #5e8a48;
}

.contact_cont .contact_attach img {
  flex: none;
  height: 54px;
  margin-left: auto;
  border: 1px solid var(--win-border);
  border-radius: 4px;
  transition: opacity var(--t-fast);
}

.contact_cont .contact_attach img.off {
  opacity: 0.3;
}

/* The form fits its window whatever the room (owner 2026-10-25, an iPad: the attach row pushed the e-mail and Send
   out of the fixed-height window): the message box takes what the other rows leave, at least 80 px; the e-mail and
   Send share the last row. */
.lbx_info .tab_contents,
.lbx_info .contact_cont,
.lbx_info #contact_form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.lbx_info .contact_cont {
  box-sizing: border-box;
}

.lbx_info .contact_form {
  display: grid;
  flex: 1 1 auto;
  min-height: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: minmax(80px, 1fr);
  grid-auto-rows: auto;
  column-gap: 16px;
}

.lbx_info .contact_form > textarea,
.lbx_info .contact_form > .contact_short,
.lbx_info .contact_form > .contact_attach {
  grid-column: 1 / -1;
}

.lbx_info .contact_form > textarea {
  height: auto;
  min-height: 0;
  resize: none;
}

.lbx_info .contact_cont #contact_email {
  width: 100%;
  max-width: 560px;
  box-sizing: border-box;
}

/* "message sent" (restyle 2026-10-23, owner): a centred card with a check badge and the Back button right
   under it, instead of a grey strip with the button 125 px lower at the far right. The badge green is the
   on-tint darkened to 3.9:1 against its white check (the #91b57b tint alone is 2.3:1); the text stays body
   grey (#444 on the panel) */
.contact_cont #contact_sent {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  min-height: 300px;
  padding: 16px 0;
  overflow-wrap: anywhere;
}

.contact_cont .contact_sent {
  box-sizing: border-box;
  width: min(100%, 440px);
  margin: 0;
  padding: 26px 24px 24px;
  background: var(--win-panel);
  border: 1px solid var(--win-border);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgb(0 0 0 / 8%);
  color: var(--text-2);
  font-size: 18px;
  line-height: 1.4;
  text-align: center;
}

.contact_cont .contact_sent::before {
  content: "";
  display: block;
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: #5e8a48 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5 12.5l4.2 4.2L19 7' fill='none' stroke='%23fff' stroke-width='2.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / 30px no-repeat;
}

.contact_cont #contact_sent #contact_back {
  float: none;
  margin: 0;
}

/* ---- Pause / Win tables ---------------------------------------------------------------------- */
.lbx_win table,
.lbx_pause table,
.lbx_multiplayer_win table {
  background: var(--win-panel);
  border: 1px solid var(--win-border);
  border-radius: 10px;
}

.lbx_pause .pause_cont {
  padding: 24px 16px 8px;
}

.lbx_pause td.td_content {
  color: var(--text);
  font-size: var(--fs-pause);
}

/* the Win window's own look is win.css (the celebration); these are the shared table rules */
.lbx_win .win_cont {
  position: relative;
  padding-top: 16px;
}

.lbx_win table {
  width: 734px;
  padding-left: 325px;
  background-image: none;
}

.lbx_win td,
.lbx_multiplayer_win td {
  color: var(--text-2);
}

.lbx_win td.td_score {
  color: var(--text);
}

.lbx_win td.td_best_score,
.lbx_multiplayer_win td.td_best_score {
  color: var(--text-3);
  font-size: 13px;
}

/* multiplayer result: flat gold tint + bar, no text-shadow */
.lbx_multiplayer_win .winner {
  background: var(--gold-tint);
  border: 0;
  border-left: 4px solid var(--gold);
  border-radius: 0 10px 10px 0;
  height: auto;
  padding: 10px;
}

/* rooms loading (owner 2026-10-18: "zostało w ogóle pominięte"): no white box, the game's turning ring of
   suits (app.css .indicator__spin) at 64 px and the text under it, in the middle of the window */
.lbx_multiplayer .options_window > .multi_loading {
  width: auto;
  margin: 0;
  padding: 24px;
  border: 0;
  border-radius: 0;
  background: none;
  align-items: center;
  justify-content: center;
  gap: 22px;
  color: var(--text-2);
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.multi_loading_spin {
  position: relative;
  width: 64px;
  height: 64px;
  animation: indicator-spin 1.6s linear infinite;
}

.multi_loading_suit {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 26px;
  height: 26px;
  margin: -13px 0 0 -13px;
  color: var(--text);
  font:
    700 26px / 26px Georgia,
    'Times New Roman',
    serif;
  text-align: center;
}

.multi_loading_suit--0 {
  transform: translateY(-22px);
}

.multi_loading_suit--1 {
  transform: rotate(90deg) translateY(-22px) rotate(-90deg);
}

.multi_loading_suit--2 {
  transform: rotate(180deg) translateY(-22px) rotate(-180deg);
}

.multi_loading_suit--3 {
  transform: rotate(270deg) translateY(-22px) rotate(-270deg);
}





/* room cards: the legacy 10 / 11 / 12 px captions (#666, #888) and the #999 "waiting for player"
   name are raised to the BRIEF's floor (13 px, no lighter than #555); a filled slot keeps the
   legacy blue name (#2c6594, 5.3:1 on #eee) */
.lbx_multiplayer div.multi_game_label {
  margin-top: 2px; /* the legacy -6px lifted the 11 px label; 2 px centres the 13 px one on the room label */
  color: var(--text-3);
  font-size: 13px;
}

.lbx_multiplayer div.multi_player_label {
  color: var(--text-3);
  font-size: 13px;
}

.lbx_multiplayer .multi_vs .game_time {
  color: var(--text-3);
  font-size: 13px;
}

.lbx_multiplayer div.multi_player_name.empty {
  color: var(--text-3); /* #555, 6.5:1: still reads as "waiting", unlike the 2.5:1 #999 */
}

/* ---- Themes: solid label strip, flat 44 px pager ------------------------------------------------ */
.skin_item div.label_bg {
  height: 30px;
  top: -7px;
  padding-left: 14px;
  background: rgb(0 0 0 / 55%);
  opacity: 1;
  font-size: 15px;
  font-weight: 600;
  line-height: 30px;
}

.skin_item div.label_bg .skin_item_default {
  width: auto;
  height: 20px;
  margin: 5px 8px 0 0;
  padding: 0 10px;
  background: rgb(255 255 255 / 88%);
  border-radius: 6px;
  color: var(--text);
  font-size: 13px;
  line-height: 20px;
}

/* Theme tiles edge to edge (owner 2026-10-17: "są jakieś gapy"): no dotted line between them, the label
   strip pinned to the bottom edge (it sat 7 px up, a band of background showed under it), and the selected
   tile marked by a blue label strip and a gold frame instead of the 4 px blue frame, which merged with the
   blue tab bar above and read as a gap around the tile */
.skin_item.select_theme {
  position: relative;
  overflow: hidden;
  border-bottom: 0;
}

.skin_item.select_theme div.label_bg {
  position: absolute;
  top: auto;
  right: 0;
  bottom: 0;
  left: 0;
}

.skin_item.selected {
  outline: 0;
  box-shadow: none;
}

.skin_item.selected div.label_bg {
  background: rgb(44 101 148 / 92%); /* --win-row-bar */
}

/* Three Mahjong tiles of the theme's set to the right of the cards (nth-of-type counts the four cards and the back first) (owner 2026-10-31: "delikatny podgląd", the deck
   option picks the tile set too), small and a step apart, out of the label strip; the flat pictures, so no painted
   shadow lands on the background. On a phone the fan fills the row, so one tile stands in the free corner on the left. */
.select_theme img.tile {
  position: absolute;
  top: 28px;
  width: 78px;
  height: 100px;
  left: calc(50% + 160px);
  z-index: 0;
}
.select_theme img.tile:nth-of-type(7) {
  left: calc(50% + 206px);
}
.select_theme img.tile:nth-of-type(8) {
  left: calc(50% + 252px);
}
#mobile .select_theme img.tile {
  display: none;
}
#mobile .select_theme img.tile:nth-of-type(6) {
  display: block;
  top: 16px;
  left: 14px;
  width: 62px;
  height: 80px;
}

/* Kulki's balls on a theme tile (owner 2026-11-03: "daj podgląd w wyglądzie"): three in a column right of the tiles; on a
   phone a small row under the one tile shown */
.select_theme .theme_balls {
  position: absolute;
  top: 30px;
  left: calc(50% + 346px);
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 0;
}
.select_theme .theme_balls img.ball {
  width: 30px;
  height: 30px;
}
#mobile .select_theme .theme_balls {
  top: 102px;
  left: 14px;
  flex-direction: row;
  gap: 3px;
}
#mobile .select_theme .theme_balls img.ball {
  width: 19px;
  height: 19px;
}

/* the selected tile's frame (owner 2026-10-17: "obramowanie zaznaczenia ... lepiej widoczne"): 4 px gold
   with a thin dark line inside, so it reads on the light and the dark backgrounds alike; drawn over the
   cards and the label */
.skin_item.select_theme.selected::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  border: 4px solid #f2c230;
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 45%);
  pointer-events: none;
}

/* Retina: the hand-cut 1x previews have no HD art, so a 2x screen gets the real table background at
   about one image pixel per screen pixel (ThemesWindow TILE_HD_POS picks the part), under the cards */
@media (min-resolution: 2dppx) {
  .skin_item.select_theme::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--tile-hd) var(--tile-pos, center) / cover no-repeat;
  }
}

.theme_item_navi {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0 8px;
  background: rgb(0 0 0 / 55%);
  border-radius: 10px;
  box-shadow: none;
  color: #fff;
  font-size: 0;
  line-height: 44px;
  opacity: 1;
}

.theme_item_navi.no_more_item {
  opacity: 0.3;
}

.theme_item_navi::before {
  content: '';
  width: var(--icon-xl);
  height: var(--icon-xl);
  background: currentColor;
  mask: var(--i-chevron-left) center / contain no-repeat;
}

.theme_item_navi[id$='_r']::before {
  mask-image: var(--i-chevron-right);
}

/* ---- ads box, stats chrome --------------------------------------------------------------------- */
/* The ad room (BRIEF item 2a): the box keeps the room of the tallest creative (--ad-slot-h) plus the
   vendor line ("Ads by optAd360", inside the creative's frame), and the ADVERTISEMENT label stays
   (owner 2026-10-16). Games: .ads is a flex sibling between the list scroller and the Play row,
   so it is always above the action row. Win: .ads is the last child of .win_cont inside the
   scroller, so on a short viewport it would scroll under the sticky button row; it is pinned to
   the bottom of the scroller instead (sticky above the 69 px action row) and the table scrolls
   under it. */
.lbx_window .ads,
.lbx_games .ads,
.lbx_multiplayer.mp_games .ads,
.lbx_win .ads,
.lbx_ranking .ads {
  padding: 0;
  background: var(--win-box);
  border: 0;
  border-top: 1px solid var(--win-hair);
  border-radius: 0;
}

.lbx_window .ads::after {
  padding: 3px 7px 3px 6px;
  background: rgb(0 0 0 / 10%);
  color: var(--text-3);
  font-size: 12px;
  border-radius: 0 0 6px 0;
}

.lbx_win .win_cont {
  flex: 1 0 auto;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lbx_win .win_cont table {
  flex: none;
}

.lbx_win .ads {
  position: sticky;
  bottom: var(--win-action-h);
  z-index: 1;
  align-self: stretch;
  margin-top: auto;
  padding-top: 12px;
  border-bottom: 1px solid var(--win-hair);
}

.stats_results .filter {
  font-size: 13px;
}

.stats_results .filter span {
  padding: 4px 10px;
  background: #5c7849; /* white on it 4.9:1 (the legacy #789666 chip was 3.3:1) */
  border-radius: 12px;
}

.stats_results .filter span.change_filtr {
  background: var(--accent);
}

.stats_results td {
  color: var(--text-2);
}

.stats_filter {
  box-shadow: 6px 0 12px -8px rgb(0 0 0 / 35%);
  border-right: 1px solid var(--win-border);
}

.lbx_auth_dashboard .tab_buttons a,
.lbx_ranking .tab_buttons a {
  max-width: 240px;
}

/* ---- the desktop tier grows (BRIEF item 11): every window 30-50 % bigger, and the content uses the
   room: 17 px body, 54 px rows, 46 px tabs, 56 px header, taller Games rows with 160 px previews (the
   JPGs are 160 x 90 natural, so no upscaling). The tokens are set on .lbx_window (not on :root), so
   they beat the inherited ones; the Win window (win.css, its own tokens and type) is left out with
   :not(.lbx_win). Scoped to the desktop box tier (840+ wide and 615+ high), so short or narrow windows
   (the full-screen sheet tier) are unchanged. The
   boxes keep min(--lbx-w, 100vw - 32px) and the engine's max-height (100dvh - band - 32px), so
   1280 x 600 / 800 x 600 clamp them. */
@media (min-width: 840px) and (min-height: 615px) {
  .lbx_window:not(.lbx_win) {
    --win-header-h: 56px;
    --win-row-h: 54px;
    --fs-body: 17px;
    --fs-header: 20px;
    --fs-tab: 17px;
    --fs-label: 14px;
    --icon-xl: 26px;
  }

  /* the sizes: legacy x ~1.4 (Options / Themes / Info / Flags 677 x 422 -> 950 x 600, Ranking and
     the dashboard 800 x 608 -> 1100 x 850, Multiplayer 740 x 540 -> 1040 x 760, Auth 487 x 306 ->
     680 x 430 minimum, Rules 640 -> 900, Confirm / Alert 640 -> 760, What's new 500 -> 700; Games
     keeps its 960 width and grows to 900 tall, clamped on a 900 px viewport) */
  .lbx_options,
  .lbx_opt_skin,
  .lbx_info,
  .lbx_flags {
    --lbx-w: 950px;
    --lbx-h: 600px;
  }

  .lbx_ranking,
  .lbx_auth_dashboard {
    --lbx-w: 1100px;
    --lbx-h: 850px;
  }

  .lbx_multiplayer {
    --lbx-w: 1040px;
    --lbx-h: 760px;
  }

  .lbx_auth {
    --lbx-w: 680px;
    --lbx-h: 430px;
  }

  .lbx_rules {
    --lbx-w: 900px;
  }

  .lbx_confirm,
  .lbx_alert {
    --lbx-w: 760px; /* one-line messages looked lost at 840 */
  }

  .lbx_games,
  .lbx_multiplayer.mp_games,
  .lbx_games.lbx_ads_big,
  .lbx_multiplayer.mp_games.lbx_ads_big,
  .lbx_games.no-ads,
  .lbx_multiplayer.mp_games.no-ads {
    --lbx-h: 900px;
  }

  /* header: 56 px, 20 px title, a 20 px gutter (the close keeps its 40 px box at right: 4px) */
  .lbx_header {
    padding-left: 20px;
  }

  /* tabs: 46 px */
  .lbx_window .tab_buttons {
    gap: 6px;
    padding: 10px 16px 0;
  }

  .lbx_window .tab_buttons a {
    height: 46px;
    padding: 0 14px;
  }

  /* option rows: 54 px, a 20 px gutter, the mark at 20 px */
  .option_item .option_item_entry {
    padding: 10px 64px 10px 20px;
  }

  .radio .option_item_entry:not(.disable_bg)::after,
  .checkbox .option_item_entry::after {
    right: 20px;
  }

  .option_item .header_label {
    padding: 18px 20px 6px;
  }

  .lbx_flags .option_item {
    gap: 4px;
    padding: 12px;
  }

  .lbx_flags .radio .option_item_entry {
    width: 48px;
    min-height: 48px;
    padding: 0;
  }

  /* Games: 112 px rows, 160 px previews, 18 px names, 15 px descriptions. A grid (preview | name
     and description | stars) instead of the legacy floats: with the bigger preview and the PL / DE
     names wrapping to two lines, a third description line fell under the floated preview and its
     "more" link landed at the left edge (seen on the 802 px box of an iPad in portrait). */
  .games_window .option_item .option_item_entry {
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr) auto;
    grid-template-rows: auto auto;
    column-gap: 18px;
    align-items: start;
    min-height: 112px;
    padding: 14px 64px 14px 20px;
  }

  .option_item .option_item_entry .game_preview {
    grid-column: 1;
    grid-row: 1 / span 2;
    float: none;
    width: 160px;
    margin: 0;
    border-radius: 8px;
  }

  .games_window .game_name {
    grid-column: 2;
    grid-row: 1;
  }

  .radio .option_item_entry .game_desc {
    grid-column: 2;
    grid-row: 2;
  }

  .radio .option_item_entry span.stars {
    grid-column: 3;
    grid-row: 1 / span 2;
    float: none;
    text-align: center;
  }

  .games_window .game_name {
    font-size: 18px;
    line-height: 22px;
  }

  .radio .option_item_entry .game_desc {
    margin-top: 4px;
    font-size: 15px;
    line-height: 21px;
  }

  .radio .option_item_entry span.stars {
    margin: 3px 0 0 16px;
  }

  .radio .option_item_entry span.stars .caption {
    margin-top: 4px;
    font-size: 14px;
  }

  .radio .option_item_entry span.stars img {
    width: 18px;
    height: 18px;
  }

  /* buttons: 17 px type (the 44 px height and the 69 px action row are kept: the Win ad pin reads it) */
  .lbx_window input[type='button'],
  .lbx_window input[type='submit'] {
    font-size: 17px;
  }

  .lbx_games .game_button_cont input,
  .lbx_multiplayer.mp_games .game_button_cont input {
    min-width: 280px;
    font-size: 21px;
  }

  /* text boxes: 20 px margins, 16 / 20 px padding, 1.5 leading */
  .rules_cont,
  .lbx_cont_row .rows,
  .dashboard_cont .rows {
    margin: 20px;
    padding: 16px 20px;
    line-height: 1.5;
  }

  .lbx_confirm .rules_cont,
  .lbx_alert .rules_cont {
    margin: 28px 28px 12px;
    padding: 0;
    line-height: 1.55;
  }

  /* forms: 17 px inputs, 14 px helper, roomier rows */
  .lbx_window input[type='text'],
  .lbx_window input[type='email'],
  .lbx_window input[type='password'],
  .lbx_window textarea,
  .lbx_window select {
    padding: 0 14px;
    font-size: 17px;
  }

  .lbx_window textarea {
    height: 240px;
    padding: 12px 14px;
  }

  .auth_register .row,
  .auth_login .row {
    gap: 12px;
    margin-bottom: 14px;
  }

  .register_desc,
  .login_desc {
    margin-bottom: 18px;
  }

  .auth_valid_info {
    margin-top: 6px;
    font-size: 14px;
  }

  .lbx_buttons {
    padding: 14px 20px;
  }

  /* Info (contact form) */
  .contact_cont {
    padding: 20px;
  }

  .contact_cont textarea,
  .contact_cont #contact_email {
    margin-top: 14px;
  }

  .contact_cont #contact_send,
  .contact_cont #contact_back {
    min-width: 160px;
    margin-top: 14px;
    font-size: 17px;
  }

  /* Pause: a 640 px card, 44 px message */
  .lbx_pause .pause_cont {
    padding: 32px 24px 12px;
  }

  .lbx_pause table {
    width: 640px;
    max-width: 100%;
    box-sizing: border-box;
    padding: 20px 28px;
  }

  .lbx_pause td.td_content {
    font-size: 44px;
  }

  /* Themes: 34 px label strip, 16 px label */
  .skin_item div.label_bg {
    height: 34px;
    top: -9px;
    padding-left: 16px;
    font-size: 16px;
    line-height: 34px;
  }

  .skin_item div.label_bg .skin_item_default {
    height: 22px;
    margin-top: 6px;
    font-size: 14px;
    line-height: 22px;
  }

  /* Ranking / dashboard: 14 px table, 6 / 12 px cells, 14 px chips */
  .stats_results table {
    font-size: 14px;
  }

  .stats_results td {
    padding: 6px 12px;
  }

  .stats_results th,
  .stats_results thead th.thin {
    padding: 6px 15px;
  }

  .stats_results .filter {
    font-size: 14px;
  }

  .stats_filter .option_item .option_item_entry {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  /* Multiplayer room cards: 14 px captions */
  .lbx_multiplayer div.multi_game_label,
  .lbx_multiplayer div.multi_player_label,
  .lbx_multiplayer .multi_vs .game_time {
    font-size: 14px;
  }
}

/* ---- short desktop windows (<= 500 px tall): 40 px header ------------------------------------- */
@media (max-height: 500px) {
  .lbx_window {
    --win-header-h: 40px;
  }

  /* the 40 px plate filled the 40 px header edge to edge; a 32 px plate with 4 px of header above and
     below it instead (the glyph stays 20 px: the ::before box is unchanged) */
  .lbx_window .lbx_close,
  .lbx_window .lbx_minimize {
    top: 4px;
    right: 8px;
    width: 32px;
    height: 32px;
  }

  .lbx_window .lbx_minimize {
    right: 44px;
  }
}

/* ---- the sheet tier: every window a rising full sheet (BRIEF item 6, no bottom cards) ---------- */
@keyframes sheet-fade {
  from {
    opacity: 0;
  }
}

@keyframes sheet-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 559px) {
  .lbx_window {
    --win-header-h: 52px;
  }

  /* Ranking / dashboard stats: a compact table that fits the sheet (the legacy 94 % + 20 px margins
     and 15 px cell padding overflowed a 412 px sheet with the Score column cut and two columns off
     screen); Moves (8) and Created at (9) are dropped under 560 px, the scroller keeps overflow auto
     as the fallback for long player names */
  .stats_results table {
    width: calc(100% - 16px);
    margin: 8px;
    font-size: 13px;
  }

  .stats_results th {
    padding: 4px 5px;
  }

  .stats_results thead th.thin {
    padding: 4px 3px;
  }

  .stats_results td {
    padding: 4px 5px;
  }

  .stats_results th:nth-child(8),
  .stats_results td:nth-child(8),
  .stats_results th:nth-child(9),
  .stats_results td:nth-child(9) {
    display: none;
  }

  .stats_results .filter,
  .stats_results .legend {
    margin-left: 8px;
    margin-right: 8px;
  }
}

@media (max-width: 839px), (max-height: 614px) {
  .lbx_window {
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  /* a full-screen window fades in as a whole while its content rises 10 px, and leaves the same way, quick
     and smooth (owner 2026-10-18: it looked pulled up to the top edge: the box was opaque at once while the
     content slid 24 px). The box fades by an animation, so the e2e rect checks are not moved by it. */
  .lbx_window.lbx_showed {
    animation: sheet-fade 200ms ease-out backwards; /* not `both`: a kept end value stops the close transition */
  }

  .lbx_window.lbx_showed .lbx_container {
    animation: sheet-in 240ms cubic-bezier(0.2, 0.9, 0.3, 1) backwards;
  }

  .lbx_window:not(.lbx_showed) .lbx_container {
    transform: translateY(10px);
    transition: transform var(--t-close) ease-in;
  }

  /* same selector as the plate rule above (0,3,1), so the safe-area offset still wins */
  .lbx_window .lbx_close,
  .lbx_window .lbx_minimize {
    top: calc((var(--win-header-h) - 40px) / 2 + env(safe-area-inset-top, 0px));
  }

  /* A long Rules title ("Klondike - Rozdawanie po jednej karcie", 409 px) was ellipsised on a
     320-400 px sheet; it wraps to two lines instead (the engine's header is min-height, so it grows;
     the close stays in the first 52 px). The safe-area top offset is restated because this padding
     shorthand replaces the engine's. */
  .lbx_header {
    padding: calc(8px + env(safe-area-inset-top, 0px)) 56px 8px 16px;
    white-space: normal;
    overflow-wrap: anywhere;
    text-overflow: clip;
  }

  /* Confirm / Alert: the message block breathes at the top of the full sheet; the engine pins the
     button row at the bottom (margin-top: auto) */
  .lbx_confirm .rules_cont,
  .lbx_alert .rules_cont {
    margin: 20px 16px 8px;
    font-size: 16px;
  }

  /* Pause: a full sheet, the message centred between the header and the pinned button */
  .lbx_pause .pause_cont {
    margin-top: auto;
    padding: 16px;
  }

  .lbx_pause table {
    width: auto;
    max-width: 100%;
    padding: 12px 16px;
  }

  .lbx_pause td.td_content {
    font-size: 28px;
  }

  /* Games: 80 px rows, 90 px previews; a small grid (preview | name / stars, description below)
     instead of the desktop floats, so the names stay on one line beside the stars */
  .games_window .option_item .option_item_entry {
    display: grid;
    grid-template-columns: 90px 1fr;
    grid-template-rows: auto auto auto;
    column-gap: 10px;
    row-gap: 2px;
    align-items: start;
    min-height: 80px;
    padding: 10px 52px 10px 16px; /* 16, not 12: room for the 6 px selection bar before the preview */
  }

  .option_item .option_item_entry .game_preview {
    grid-row: 1 / span 2;
    float: none;
    width: 90px;
    margin: 0;
  }

  /* the PL names ("Podwójny Klondike - Rozdawanie po jednej karcie") do not fit the 248 px column:
     they wrap to two lines (the grid rows are auto, the preview spans rows 1-2 and the stars sit
     in row 2 under the name); an ellipsis would hide the variant word that tells the rows apart */
  .games_window .game_name {
    grid-column: 2;
    grid-row: 1;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .radio .option_item_entry span.stars {
    grid-column: 2;
    grid-row: 2;
    display: flex;
    align-items: center;
    float: none;
    margin: 0;
  }

  .radio .option_item_entry span.stars .caption {
    display: inline;
    clear: none;
    margin: 0 0 0 6px;
  }

  .radio .option_item_entry .game_desc {
    grid-column: 1 / -1;
    grid-row: 3;
    margin-top: 4px;
  }

  .radio .option_item_entry span.stars img {
    width: 14px;
    height: 14px;
    margin-left: 1px;
  }

  /* forms stack */
  .auth_register .row,
  .auth_login .row {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    text-align: left;
  }

  .auth_register .row span,
  .auth_login .row span {
    flex: none;
    width: auto;
  }

  .auth_register .row input,
  .auth_login .row input {
    width: 100%;
  }

  .contact_cont #contact_email {
    width: 100%;
  }

  /* a phone: the e-mail on its own row, Send under it on the right */
  .lbx_info .contact_form {
    grid-template-columns: minmax(0, 1fr);
  }

  .lbx_info .contact_form > #contact_send {
    justify-self: end;
  }

  .lbx_games .game_button_cont input,
  .lbx_multiplayer.mp_games .game_button_cont input,
  .rules_buttons input,
  .lbx_pause .win_buttons input,
  .lbx_win .win_buttons input {
    flex: 1 1 0;
    min-width: 0;
    max-width: 420px;
  }
}

/* 320-419 px: the three Options tabs ("Einstellungen", "Ustawienia") clipped at 96 px each */
@media (max-width: 419px) {
  .lbx_window .tab_buttons {
    gap: 2px;
    padding: 8px 8px 0;
  }

  .lbx_window .tab_buttons a {
    flex: 1 1 auto; /* the free space follows the label length, so the long tab gets more */
    padding: 0 6px;
    font-size: 14px;
  }
}

/* short landscape: the phone-portrait header height does not apply, 40 px does */
@media (max-height: 500px) {
  .lbx_window {
    --win-header-h: 40px;
  }

  .lbx_window textarea {
    height: 96px;
  }

  /* The 32 px plate of the short tier, restated after the sheet-tier block above (same selector,
     same (0,3,1)) which had put `top` back to the 40 px formula; the safe-area offset is kept. */
  .lbx_window .lbx_close,
  .lbx_window .lbx_minimize {
    top: calc(4px + env(safe-area-inset-top, 0px));
    right: 8px;
    width: 32px;
    height: 32px;
  }

  .lbx_window .lbx_minimize {
    right: 44px;
  }
}

/* ---- Ranking window (owner 2026-10-17: "popraw wygląd okna rankingu dla niezalogowanych", an ad slot,
   "guzik filtra się przesuwa - nie powinien") --------------------------------------------------------
   One column that does not scroll: the filter bar on top (the summary chips and Change filter, which used
   to be absolute and floated over the rows as the table scrolled), the table in a rounded white frame as
   the only scroller with its header row pinned, then the legend (the ad slot 4 it had at first was dropped:
   Optad360 has no slot 4, owner 2026-10-17).
   The DOM is the tests' (StatsPanel): only the order and the look change. The Dashboard's Your stats and
   Global stats tabs share it (owner 2026-10-17: "filtr i stylowanie jak w rankingu"). */
:is(.lbx_ranking, .lbx_auth_dashboard) .tab_contents:is(.global, .personal):not([style*='none']) {
  display: flex !important; /* RankingWindow and DashboardTabs set display: block | none inline */
  flex-direction: column;
  overflow: hidden;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
}

/* StatsPanel shows / hides the results with an inline display: block | none */
:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results:not([style*='none']) {
  display: flex !important;
  flex-direction: column;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .rows.filter {
  order: -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 14px 16px 10px;
  padding: 0;
  background: none;
  border: 0;
  font-size: 14px;
  text-align: left;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .filter span {
  margin: 0;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid var(--win-border);
  border-radius: 999px;
  color: var(--text-3);
  line-height: 1.3;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .filter span strong {
  color: var(--text);
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .filter span.change_filtr {
  position: static; /* in the bar, never over the rows */
  margin-left: auto;
  padding: 9px 18px;
  background: var(--accent);
  border-color: var(--accent);
  border-radius: 10px;
  color: #fff;
  font-size: 15px;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .filter:hover span.change_filtr {
  background: var(--accent-hi);
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_table_cont {
  margin: 0 16px;
  background: #fff;
  border: 1px solid var(--win-border);
  border-radius: 10px;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results table {
  width: 100%;
  margin: 0;
  border: 0;
  border-collapse: separate; /* collapsed borders vanish under a sticky header */
  border-spacing: 0;
  background: #fff;
  font-size: 15px;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 8px;
  border: 0;
  background: #3d7aad;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td {
  padding: 9px 8px;
  border: 0;
  border-bottom: 1px solid #e6e6e6;
  background: #fff;
  color: var(--text-2);
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results tr.odd td {
  background: #f6f7f8;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results tbody tr:hover td {
  background: #eaf1f7;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results tbody tr:last-child td {
  border-bottom: 0;
}

/* the player: the name and the flag on one line (the cell's <br> is the tests' markup) */
:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td:nth-child(2) {
  color: var(--text);
  font-weight: 600;
  text-align: left;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td:nth-child(2) br {
  display: none;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td:nth-child(2) img {
  padding: 0 1px 2px;
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td:nth-child(1) {
  color: var(--text-3);
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td b {
  color: var(--text);
}

:is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .rows.legend {
  margin: 8px 16px 12px;
  padding: 0;
  background: none;
  border: 0;
  color: var(--text-3);
  font-size: 13px;
}

/* the filter panel (StatsPanel, also the Dashboard's): its sections one under another; Cancel and Ok in one
   row pinned to the bottom of the panel's
   scroller (owner 2026-10-17: the panel "się krzaczy": a 335 px strip with the rest of the window empty,
   Cancel out of sight) */
.stats_filter .filter_sections {
  display: grid;
  grid-template-columns: minmax(0, 1fr); /* one column (owner 2026-10-17: two looked "jakby moduł się źle załadował") */
  gap: 4px;
  padding: 6px 16px 16px;
}

.stats_filter .filter_section .option_item.label {
  margin: 10px 0 0;
}

.stats_filter .filter_actions {
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--win-bg);
  border-top: 1px solid var(--win-hair);
}

.stats_filter .filter_actions .stats_ok_filtr_button,
.stats_filter .filter_actions .stats_cancel_filtr_button {
  position: static;
  display: block;
  margin: 0;
  padding: 0;
  background: none;
  border: 0;
}

.stats_filter .filter_actions input {
  min-width: 160px;
  height: 48px;
  margin: 0;
  border-radius: 10px;
  font-size: 17px;
  font-weight: 700;
}

.stats_filter .filter_actions .stats_cancel_filtr_button input {
  background: #fff;
  color: var(--accent-lo);
  box-shadow: inset 0 0 0 1px var(--accent);
}

/* the panel is a drawer over the results (owner 2026-10-17: "ładna animacja i jako overlay nad danymi"):
   the results stay, dimmed and a little blurred under a scrim (a tap on it is Cancel), the panel slides in
   from the right, one column of options, the full width on a phone */
.dashboard_cont .tab_contents {
  position: relative;
}

.stats_scrim {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgb(0 0 0 / 28%);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
  animation: stats-fade-in 220ms ease-out both;
}

.stats_scrim[data-closing] {
  animation: stats-fade-out 200ms ease-in both;
}

.stats_filter,
:is(.lbx_ranking, .lbx_auth_dashboard) .stats_filter {
  top: 0;
  right: 0;
  bottom: 0;
  left: auto;
  z-index: 3;
  width: min(460px, 100%);
  border-right: 0;
  border-left: 1px solid var(--win-border);
  box-shadow: -12px 0 28px rgb(0 0 0 / 22%);
  animation: stats-drawer-in 280ms cubic-bezier(0.2, 0.9, 0.3, 1) both;
}

.stats_filter[data-closing] {
  animation: stats-drawer-out 200ms ease-in both;
}

.stats_filter:not([style*='none']) {
  display: flex !important;
  flex-direction: column;
}

.stats_filter .filter_sections {
  flex: 1 0 auto;
  align-content: start;
}

@keyframes stats-drawer-in {
  from {
    transform: translateX(100%);
  }
}

@keyframes stats-drawer-out {
  to {
    transform: translateX(100%);
  }
}

@keyframes stats-fade-in {
  from {
    opacity: 0;
  }
}

@keyframes stats-fade-out {
  to {
    opacity: 0;
  }
}

/* phones: the five columns that matter (No., Player, Game, Time, Score); the S/N letters, moves and the
   date need more width than a 412 px sheet has */
@media (max-width: 559px) {
  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .rows.filter {
    margin: 10px 8px 8px;
    font-size: 13px;
  }

  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .filter span.change_filtr {
    padding: 8px 14px;
    font-size: 14px;
  }

  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_table_cont {
    margin: 0 8px;
  }

  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results table {
    font-size: 13px;
  }

  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results th:nth-child(4),
  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td:nth-child(4),
  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results th:nth-child(5),
  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td:nth-child(5) {
    display: none;
  }

  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results thead th,
  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td {
    padding: 8px 4px;
  }

  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results thead th {
    font-size: 12px;
  }

  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results .rows.legend {
    margin: 6px 8px 8px;
  }

  /* no wrapped text on phones (owner 2026-10-17): a row is one line; a column that does not fit scrolls
     sideways inside the table's frame */
  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results th,
  :is(.lbx_ranking, .lbx_auth_dashboard) .stats_results td {
    white-space: nowrap;
  }
}

/* ---- Dashboard, the Panel tab (owner 2026-10-17: "kontent się nie mieści w oknie, więc pojawia się scroll";
   "przycisk wyloguj zrobiłbym większy i jak button a nie jak link") ---------------------------------------
   A grid instead of the legacy floats: the player box and the chart on the left, the summary on the right
   at the full height, and Log out in the window's action row under both (it was an absolute 13 px link in
   the corner). Tighter margins and type so a 1024 x 768 tablet shows the whole tab without a scrollbar. */
.dashboard_panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); /* halves: the summary lines wrap less */
  grid-template-rows: 1fr auto;
  min-height: 100%;
}

.dashboard_panel .dashboard_main,
.dashboard_panel .dashboard_details {
  float: none;
  width: auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 7px 14px 14px;
}

.dashboard_panel .dashboard_details {
  padding: 14px 14px 14px 7px;
}

.dashboard_panel .dashboard_main .rows,
.dashboard_panel .dashboard_details .rows {
  margin: 0;
  padding: 12px 16px;
  line-height: 1.4;
}

.dashboard_panel .dashboard_main .rows:last-child,
.dashboard_panel .dashboard_details .rows {
  flex: 1 1 auto;
}

.dashboard_panel .row {
  margin: 0;
}

.dashboard_panel .welcome_player {
  margin: 0 0 10px;
  font-size: 24px;
  text-align: center;
}

.dashboard_panel .info_desc {
  margin-bottom: 6px;
}

.dashboard_panel .info_desc:last-child {
  margin-bottom: 0;
}

.dashboard_panel .most_played_label,
.dashboard_panel .medium_label {
  margin: 0 0 4px;
  font-size: 20px;
}

.dashboard_panel .dashboard_details ul li {
  padding: 6px 0;
}

.dashboard_panel .panel_logout {
  grid-column: 1 / -1;
  position: sticky;
  bottom: 0;
  z-index: 1;
  float: none;
  width: auto;
  display: flex;
  justify-content: center;
  padding: 12px 16px;
  background: var(--win-bg);
  border-top: 1px solid var(--win-hair);
}

.dashboard_panel .panel_logout a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-sizing: border-box;
  height: var(--btn-h);
  min-width: 200px;
  padding: 0 24px;
  background: #fff;
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px var(--accent);
  color: var(--accent-lo);
  font-family: var(--win-font);
  font-size: 17px;
  font-weight: 600;
  text-decoration: none;
  transition: background-color var(--t-fast);
}

.dashboard_panel .panel_logout a::before {
  content: '';
  width: 20px;
  height: 20px;
  background: currentColor;
  -webkit-mask: var(--i-logout) center / contain no-repeat;
  mask: var(--i-logout) center / contain no-repeat;
}

.dashboard_panel .panel_logout a:hover {
  background: #f3f7fa;
}

.dashboard_panel .panel_logout a:active {
  background: #e6eef5;
}

/* phones: one column (player, chart, summary), the button full width */
@media (max-width: 559px) {
  .dashboard_panel {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
  }

  .dashboard_panel .dashboard_main,
  .dashboard_panel .dashboard_details {
    padding: 10px 10px 0;
    gap: 10px;
  }

  .dashboard_panel .dashboard_details {
    padding-bottom: 10px;
  }

  .dashboard_panel .welcome_player {
    font-size: 22px;
    white-space: normal;
  }

  .dashboard_panel .panel_logout a {
    width: 100%;
  }
}

/* short screens (a tablet with the browser bars and the ad band): the greeting repeats the window's
   header, so it goes and the chart gets the room */
@media (min-width: 560px) and (max-height: 820px) {
  .dashboard_panel .welcome_player {
    display: none;
  }

  .dashboard_panel .dashboard_main,
  .dashboard_panel .dashboard_details {
    padding-top: 12px;
    padding-bottom: 12px;
    gap: 10px;
  }

  .dashboard_panel .dashboard_details ul li {
    padding: 3px 0;
  }

  .dashboard_panel .panel_logout {
    padding: 10px 16px;
  }
}

/* ==== the Pause window (owner 2026-10-18: "dostyluj ładnie okno pauzy", then "w stylu grafiki winstats
   ... żeby styl był spójny" with "kawę, herbatę, ciastko losowo pokazywane za każdą pauzą"): the Win window's
   look (win.css): the warm cream body with a faint wallpaper of the player's card back, slow golden rays
   behind the hero, the title in Rye, the numbers on the same old cream tickets (notched sides, dashed print
   line), the blue primary button. The hero is an animated treat (SimpleWindows.tsx TreatArt): coffee or tea
   with rising steam, or a cookie with falling crumbs. Selectors `.lbx_window.lbx_pause ...` beat the legacy
   table rules above. ================================================================================== */
.lbx_window.lbx_pause {
  --pause-cream-hi: #fff7e2; /* the Win window's tokens (win.css) */
  --pause-cream: #f3ead2;
  --pause-cream-lo: #efe6cf;
  --pause-ticket: #fbf3dc;
  --pause-ink: #2b1a0b;
  --pause-red: #a3121f;
  --pause-line: rgb(163 18 31 / 40%);
  /* no fill on the box itself: the backdrop paints the body, and a cream box fill showed as a light rim
     around the header's rounded top corners (owner 2026-10-18) */
  background: transparent;
}

.lbx_window.lbx_pause .lbx_content {
  position: relative;
  overflow: hidden;
  background: transparent;
}

.lbx_window.lbx_pause .pause_backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg, var(--pause-cream-hi) 0%, var(--pause-cream) 45%, var(--pause-cream-lo) 100%);
}

.lbx_window.lbx_pause .pause_backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background: var(--pause-back) 0 0 / 46px auto;
}

.lbx_window.lbx_pause .tab_contents {
  position: relative;
  z-index: 1;
  overflow: auto;
  background: transparent;
}

.lbx_window.lbx_pause .pause_cont {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 18px 24px 4px;
  overflow: hidden; /* the rays stay inside: else they made the body scrollable */
  text-align: center;
}

/* the hero: the treat over slow golden rays */
.lbx_window.lbx_pause .pause_hero {
  position: relative;
  display: grid;
  place-items: center;
  width: 150px;
  height: 120px;
  animation: pause-pop 600ms cubic-bezier(0.34, 1.5, 0.64, 1) both;
}

.lbx_window.lbx_pause .pause_rays {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: -1;
  width: 520px;
  height: 520px;
  margin: -260px 0 0 -260px;
  background: repeating-conic-gradient(from 0deg, rgb(255 196 60 / 30%) 0deg 7deg, transparent 7deg 18deg);
  -webkit-mask: radial-gradient(circle, #000 14%, transparent 50%);
  mask: radial-gradient(circle, #000 14%, transparent 50%);
  animation: pause-rays 40s linear infinite;
}

.lbx_window.lbx_pause .pause_art {
  width: 120px;
  height: 120px;
  overflow: visible;
  filter: drop-shadow(0 4px 4px rgb(60 30 0 / 18%));
}

/* the steam: three wisps rising and fading one after another */
.lbx_window.lbx_pause .pause_steam path {
  fill: none;
  stroke: rgb(140 120 90 / 55%);
  stroke-width: 3.5;
  stroke-linecap: round;
  animation: pause-steam 2.4s ease-out infinite;
}

.lbx_window.lbx_pause .pause_steam path:nth-child(2) {
  animation-delay: 0.8s;
}

.lbx_window.lbx_pause .pause_steam path:nth-child(3) {
  animation-delay: 1.6s;
}

/* the cookie bobs a little; crumbs fall from its bitten edge */
.lbx_window.lbx_pause .pause_cookie {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: pause-bob 3s ease-in-out infinite;
}

.lbx_window.lbx_pause .pause_crumbs circle {
  animation: pause-crumb 2.2s ease-in infinite;
}

.lbx_window.lbx_pause .pause_crumbs circle:nth-child(2) {
  animation-delay: 0.7s;
}

.lbx_window.lbx_pause .pause_crumbs circle:nth-child(3) {
  animation-delay: 1.4s;
}

.lbx_window.lbx_pause .pause_title {
  color: var(--pause-ink);
  font:
    400 32px/1.15 Saloon,
    Georgia,
    serif;
  animation: pause-fade-up 450ms ease-out 150ms both;
}

/* the numbers: the Win window's old tickets */
.lbx_window.lbx_pause .pause_stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  width: min(420px, 100%);
  margin-top: 4px;
  filter: drop-shadow(0 4px 5px rgb(60 30 0 / 22%)); /* the mask cuts box-shadows: the shadow is here */
}

.lbx_window.lbx_pause .pause_stat {
  --notch:
    radial-gradient(circle 8px at 0 50%, transparent 97%, #000) left / 51% 100% no-repeat,
    radial-gradient(circle 8px at 100% 50%, transparent 97%, #000) right / 51% 100% no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  height: 70px;
  box-sizing: border-box;
  padding: 6px 14px;
  background: var(--pause-ticket);
  border-radius: 6px;
  outline: 2px dashed var(--pause-line);
  outline-offset: -7px;
  -webkit-mask: var(--notch);
  mask: var(--notch);
  animation: pause-stamp 400ms ease-out both;
}

.lbx_window.lbx_pause .pause_stat:nth-child(1) {
  rotate: -1.5deg;
  animation-delay: 250ms;
}

.lbx_window.lbx_pause .pause_stat:nth-child(2) {
  rotate: 1deg;
  animation-delay: 330ms;
}

.lbx_window.lbx_pause .pause_stat:nth-child(3) {
  rotate: -0.5deg;
  animation-delay: 410ms;
}

.lbx_window.lbx_pause .pause_stat span {
  color: var(--pause-red);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.2;
  font-variant-caps: all-small-caps;
  white-space: nowrap;
}

.lbx_window.lbx_pause .pause_stat b {
  color: var(--pause-ink);
  font:
    400 24px/1.15 Saloon,
    Georgia,
    serif;
  font-variant-numeric: tabular-nums;
}

.lbx_window.lbx_pause .win_buttons {
  position: relative;
  display: flex;
  justify-content: center;
  margin: 0;
  padding: 18px 24px 22px;
  border-top: 0;
  background: transparent;
}

.lbx_window.lbx_pause .win_buttons input {
  min-width: 220px;
  height: 52px;
  margin: 0;
  padding: 0 28px;
  border: 0;
  border-radius: 10px;
  background: var(--accent);
  color: #fff;
  font-size: 19px;
  font-weight: 700;
  line-height: 52px;
  cursor: pointer;
}

@keyframes pause-pop {
  from {
    opacity: 0;
    transform: scale(0.5) rotate(-10deg);
  }
}

@keyframes pause-rays {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pause-steam {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }

  30% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translateY(-14px);
  }
}

@keyframes pause-bob {
  50% {
    transform: translateY(-4px) rotate(4deg);
  }
}

@keyframes pause-crumb {
  0% {
    opacity: 0;
    transform: translate(0, 0);
  }

  15% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(4px, 46px);
  }
}

@keyframes pause-fade-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
}

@keyframes pause-stamp {
  from {
    opacity: 0;
    transform: scale(1.5) rotate(-8deg);
  }
}

/* the desktop box: a 480 px card */
@media (min-width: 840px) and (min-height: 615px) {
  .lbx_window.lbx_pause {
    width: min(480px, 100vw - 32px);
  }
}

/* the full-screen sheet: the whole group in the middle */
@media (max-width: 839px), (max-height: 614px) {
  .lbx_window.lbx_pause .tab_contents {
    justify-content: center;
  }

  .lbx_window.lbx_pause .pause_cont {
    margin-top: 0;
  }

  .lbx_window.lbx_pause .win_buttons {
    margin-top: 0;
  }

  .lbx_window.lbx_pause .win_buttons input {
    flex: 0 1 320px;
    max-width: 320px;
  }
}

/* short sheets (a phone on its side): a smaller hero */
@media (max-height: 500px) {
  .lbx_window.lbx_pause .pause_cont {
    gap: 6px;
    padding-top: 8px;
  }

  .lbx_window.lbx_pause .pause_hero {
    width: 110px;
    height: 80px;
  }

  .lbx_window.lbx_pause .pause_art {
    width: 80px;
    height: 80px;
  }

  .lbx_window.lbx_pause .pause_title {
    font-size: 26px;
  }

  .lbx_window.lbx_pause .pause_stat {
    height: 58px;
  }

  .lbx_window.lbx_pause .win_buttons {
    padding: 10px 16px 12px;
  }
}

/* ---- Options: settings cards (owner 2026-10-27, "E1", from the legacy game's colours, no brown): the Settings tab
   lies on the legacy window grey; each group is a white card with round corners under a small grey caption; the
   checkboxes are on/off switches and the chosen radio is a mark in the tabs' blue (#3d7aad) with its words in bold.
   No row fill and no pressed shadow: on the thin stacked rows they read as dark bands ("styl zaznaczenia jest
   okrutny"). The Scoring and Language tabs keep their full-width rows, marked the same way. */
.lbx_options {
  --opt-blue: #3d7aad;
  --opt-line: #d4d4d4;
}

.lbx_options .tab_contents:has(> .option_item.label) {
  padding-bottom: 16px;
  background: #e6e6e6;
}

.lbx_options .tab_contents:has(> .option_item.label) > .option_item {
  width: auto;
  margin: 0 16px;
  border-bottom: 0;
}

.lbx_options .option_item.label:not(:first-child) {
  margin-top: 10px;
}

.lbx_options .option_item .header_label {
  padding: 12px 16px 6px;
  color: #666;
  font-size: 12px;
  letter-spacing: 0.05em;
}

/* the rows: plain, a hairline between them; the card's frame and its round top and bottom */
.lbx_options .option_item_entry,
.lbx_options .option_item_entry.selected,
.lbx_options .option_item_entry.selected:hover {
  background: #fff;
  box-shadow: none;
  font-weight: 400;
}

.lbx_options .option_item_entry:hover {
  background: #f4f4f4;
}

.lbx_options .radio .option_item_entry.selected {
  font-weight: 700;
}

.lbx_options .option_item.label ~ .option_item:not(.label) .option_item_entry {
  border-right: 1px solid var(--opt-line);
  border-bottom: 1px solid var(--opt-line);
  border-left: 1px solid var(--opt-line);
}

.lbx_options .option_item.label + .option_item .option_item_entry:first-child {
  border-top: 1px solid var(--opt-line);
  border-radius: 10px 10px 0 0;
}

.lbx_options .option_item:not(.label):has(+ .option_item.label) .option_item_entry:last-child,
.lbx_options .option_item.label ~ .option_item:not(.label):last-child .option_item_entry:last-child {
  border-radius: 0 0 10px 10px;
}

/* the marks: an empty one mid grey, a chosen one the tabs' blue (4.9:1 on white); 34 px (owner 2026-10-27: +20 %) */
.lbx_options .radio .option_item_entry:not(.disable_bg)::after {
  width: 34px;
  height: 34px;
  margin-top: -17px;
  background: #888;
}

.lbx_options .radio .option_item_entry.selected::after {
  background: var(--opt-blue);
}

/* a checkbox is a switch: a grey track with a white knob on the left, the blue track with the knob on the right when
   on (drawn with gradients: the row's ::after, no extra markup) */
.lbx_options .checkbox .option_item_entry::after,
.lbx_options .checkbox .option_item_entry.selected::after {
  width: 42px;
  height: 24px;
  margin-top: -12px;
  border-radius: 12px;
  -webkit-mask: none;
  mask: none;
  background: radial-gradient(circle at 12px 50%, #fff 9px, transparent 10px), #bdbdbd;
  box-shadow: inset 0 1px 2px rgb(0 0 0 / 25%);
}

.lbx_options .checkbox .option_item_entry.selected::after {
  background: radial-gradient(circle at 30px 50%, #fff 9px, transparent 10px), var(--opt-blue);
}

/* room for the 42 px switch beside the words (the row's right padding was made for a 28-34 px mark) */
.lbx_options .checkbox .option_item_entry {
  padding-right: 76px;
}

/* a switch the browser cannot change (the Classic mode on an old browser) */
.lbx_options .checkbox .option_item_entry.locked::after {
  opacity: 0.5;
}

.lbx_options .option_item.link .option_item_entry::after {
  background: #888;
}

/* the Games window's marks +20 % too (24 to 29 px), in their blue (owner 2026-10-27) */
.lbx_games .radio .option_item_entry:not(.disable_bg)::after,
.lbx_multiplayer.mp_games .radio .option_item_entry:not(.disable_bg)::after {
  width: 29px;
  height: 29px;
  margin-top: -14.5px;
}

/* ==== Mahjong's "No moves" window (SimpleWindows.tsx NoMovesWindow; owner 2026-10-24: "popraw stylowanie okna", it
   was a grey text box over an empty window): the Pause window's look, sized to its content. The hero: three tiles
   that keep shuffling (they swap places in turn), the title in Rye, a short text, Shuffle the big blue action, Undo
   and New game the white secondary ones. ======================================================================== */
.lbx_no_moves {
  --lbx-w: 520px;
  --lbx-h: 100vh;
}

.lbx_window.lbx_no_moves {
  --pause-cream-hi: #fff7e2;
  --pause-cream: #f3ead2;
  --pause-cream-lo: #efe6cf;
  --pause-ink: #2b1a0b;
  background: transparent;
}

.lbx_window.lbx_no_moves .lbx_content {
  position: relative;
  overflow: hidden;
  background: transparent;
}

.lbx_window.lbx_no_moves .pause_backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg, var(--pause-cream-hi) 0%, var(--pause-cream) 45%, var(--pause-cream-lo) 100%);
}

.lbx_window.lbx_no_moves .pause_backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.04; /* the tile backs make a grid: fainter than the Pause window's card backs */
  background: var(--pause-back) 0 0 / 40px auto;
}

.lbx_window.lbx_no_moves .tab_contents {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: transparent;
}

.lbx_window.lbx_no_moves .nomoves_cont {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 28px 18px;
  text-align: center;
}

.lbx_window.lbx_no_moves .nomoves_hero {
  position: relative;
  width: 170px;
  height: 104px;
  animation: pause-pop 600ms cubic-bezier(0.34, 1.5, 0.64, 1) both;
}

/* three tiles fanned; every 3.6 s the outer two swap places over the middle one */
.lbx_window.lbx_no_moves .nomoves_tile {
  position: absolute;
  top: 6px;
  left: 50%;
  width: 66px;
  height: auto;
  margin-left: -33px;
  pointer-events: none;
}

.lbx_window.lbx_no_moves .nomoves_tile_0 {
  z-index: 1;
  animation: nomoves-left 3.6s ease-in-out infinite;
}

.lbx_window.lbx_no_moves .nomoves_tile_1 {
  z-index: 2;
  top: 0;
  animation: nomoves-middle 3.6s ease-in-out infinite;
}

.lbx_window.lbx_no_moves .nomoves_tile_2 {
  z-index: 1;
  animation: nomoves-right 3.6s ease-in-out infinite;
}

@keyframes nomoves-left {
  0%, 30% { transform: translateX(-52px) rotate(-12deg); }
  50% { transform: translateX(0) translateY(-14px) rotate(0deg); z-index: 3; }
  70%, 100% { transform: translateX(52px) rotate(12deg); }
}

@keyframes nomoves-right {
  0%, 30% { transform: translateX(52px) rotate(12deg); }
  50% { transform: translateX(0) translateY(10px) rotate(0deg); z-index: 0; }
  70%, 100% { transform: translateX(-52px) rotate(-12deg); }
}

@keyframes nomoves-middle {
  0%, 35% { transform: translateY(0); }
  50% { transform: translateY(6px) scale(0.96); }
  65%, 100% { transform: translateY(0); }
}

.lbx_window.lbx_no_moves .nomoves_title {
  color: var(--pause-ink);
  font:
    400 28px/1.15 Saloon,
    Georgia,
    serif;
  animation: pause-fade-up 450ms ease-out 150ms both;
}

.lbx_window.lbx_no_moves .nomoves_text {
  max-width: 400px;
  color: #4a3a26;
  font-size: 16px;
  line-height: 1.45;
  animation: pause-fade-up 450ms ease-out 250ms both;
}

.lbx_window.lbx_no_moves .rules_buttons {
  position: relative;
  z-index: 1;
  /* the backdrop shows through, as under the Win window's buttons (owner 2026-10-26) */
  background: transparent;
  border-top: 0;
  box-shadow: none;
}

.lbx_window.lbx_no_moves .rules_buttons input {
  flex: 1 1 0;
  min-width: 0;
}

/* Shuffle: the one big action, a whole row of its own; Undo and New game white under it */
.lbx_window.lbx_no_moves .rules_buttons input.nomoves_shuffle {
  flex: 1 0 100%;
  height: 52px;
  border-radius: 10px;
  font-size: 18px;
}

.lbx_window.lbx_no_moves .rules_buttons input.nomoves_other {
  background: #fff;
  color: var(--accent-lo);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.lbx_window.lbx_no_moves .rules_buttons input.nomoves_other:hover {
  background: #f3f7fa;
}

/* The card games' version (ui/lostGame.ts, owner 2026-10-26): three cards of the player's deck instead of the tiles */
.lbx_window.lbx_no_moves .nomoves_card {
  width: 62px;
  margin-left: -31px;
  border-radius: 5px;
  box-shadow: 0 2px 6px rgb(0 0 0 / 25%);
}

/* The ad box in both versions (owner 2026-10-26: the Win window's slot 2): between the text and the buttons, above the
   cream backdrop; where the screen has room the window is as wide as the Win window, so the 728x90 banner fits */
.lbx_no_moves.lbx_ads_big {
  --lbx-w: 800px;
  --lbx-k: 1; /* never the iPad's 90 % (as the Win window, win.css): a 720 px box cut the 728 / 750 px creative (owner
     2026-10-26, iPad screenshot) */
}

/* the wider window: the text runs wider and a little larger, so it fills the box instead of a narrow column (owner
   2026-10-26: "niech tekst bardziej wypełnia przestrzeń") */
.lbx_window.lbx_no_moves.lbx_ads_big .nomoves_cont {
  padding: 22px 48px 20px;
}

.lbx_window.lbx_no_moves.lbx_ads_big .nomoves_title {
  font-size: 32px;
}

.lbx_window.lbx_no_moves.lbx_ads_big .nomoves_text {
  max-width: 640px;
  font-size: 18px;
}

.lbx_window.lbx_no_moves .ads {
  position: relative;
  z-index: 1;
  margin-bottom: 4px;
  /* as in the Win window (win.css): the backdrop shows through, paler and a little blurred */
  background: rgb(255 255 255 / 30%);
  -webkit-backdrop-filter: blur(1.5px);
  backdrop-filter: blur(1.5px);
}

/* ==== Multiplayer windows of step 0b (MultiplayerWindows.tsx; owner 2026-10-29 "zrób te okna ładniejsze"): the
   leave choice, the wait after "I'm done" and the result, in the No-moves window's cream look. One scoreboard: the two
   players side by side, the points big in Saloon, mine ringed in blue, the winner's in gold with a crown. ========== */
.lbx_multiplayer_leave,
.lbx_multiplayer_done,
.lbx_multiplayer.mp_cream,
.lbx_multiplayer_win.mp_cream {
  --lbx-w: 600px;
  --lbx-h: 100vh;
  height: fit-content;
}

.lbx_multiplayer_done.lbx_ads_big,
.lbx_multiplayer_win.mp_cream.lbx_ads_big {
  --lbx-w: 800px; /* the 728 / 750 px banner fits (as the No-moves window) */
  --lbx-k: 1;
}

.lbx_window.lbx_multiplayer.mp_countdown .lbx_header {
  padding-right: 20px; /* no minimize and no close during the countdown */
}

.lbx_window.lbx_multiplayer.mp_countdown .tab_contents {
  overflow: visible;
}

.lbx_window.mp_cream {
  --pause-cream-hi: #fff7e2;
  --pause-cream: #f3ead2;
  --pause-cream-lo: #efe6cf;
  --pause-ink: #2b1a0b;
  background: transparent;
}

.lbx_window.mp_cream .lbx_content {
  position: relative;
  overflow: hidden;
  background: transparent;
}

.lbx_window.mp_cream .pause_backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg, var(--pause-cream-hi) 0%, var(--pause-cream) 45%, var(--pause-cream-lo) 100%);
}

.lbx_window.mp_cream .pause_backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background: var(--pause-back) 0 0 / 40px auto;
}

.lbx_window.mp_cream .tab_contents {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: auto;
  background: transparent;
}

.lbx_window.mp_cream .rules_buttons,
.lbx_window.mp_cream .win_buttons {
  position: relative;
  z-index: 1;
  background: transparent;
  border-top: 0;
  box-shadow: none;
}

.lbx_window.mp_cream .rules_buttons input,
.lbx_window.mp_cream .win_buttons input {
  flex: 0 1 260px;
  height: 48px;
  border-radius: 10px;
  font-size: 17px;
}

/* the leave choice's Keep playing and the wait's Leave: white, secondary */
.lbx_window.mp_cream .rules_buttons input {
  background: #fff;
  color: var(--accent-lo);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.lbx_window.mp_cream .rules_buttons input:hover {
  background: #f3f7fa;
}

.lbx_window.mp_cream .ads {
  position: relative;
  z-index: 1;
  margin-bottom: 4px;
  background: rgb(255 255 255 / 30%);
  -webkit-backdrop-filter: blur(1.5px);
  backdrop-filter: blur(1.5px);
}

.mp_cream .mp_cont {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 20px 28px 16px;
  color: var(--pause-ink);
  text-align: center;
}

.mp_cream .mp_title {
  min-height: 1.15em;
  color: var(--pause-ink);
  font:
    400 30px/1.15 Saloon,
    Georgia,
    serif;
}

.mp_cream .mp_title_in {
  animation: pause-fade-up 450ms ease-out both;
}

.mp_cream .mp_sub {
  max-width: 520px;
  margin-top: -6px;
  color: #4a3a26;
  font-size: 16px;
  line-height: 1.45;
}

.mp_cream .mp_board {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: stretch;
  gap: 10px;
  width: 100%;
  max-width: 520px;
}

.mp_cream .mp_side {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  padding: 14px 10px 12px;
  background: #fffdf6;
  border: 1px solid #d6c7a2;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgb(60 40 10 / 12%);
}

.mp_cream .mp_side.mp_me {
  border-color: var(--accent);
  box-shadow:
    inset 0 0 0 1px var(--accent),
    0 2px 8px rgb(60 40 10 / 12%);
}

.mp_cream .mp_side.mp_winner {
  background: #fff5d6;
  border-color: #b8900f;
  box-shadow:
    inset 0 0 0 1px #b8900f,
    0 2px 8px rgb(60 40 10 / 12%);
  animation: pause-pop 600ms cubic-bezier(0.34, 1.5, 0.64, 1) both;
}

.mp_cream .mp_crown {
  position: absolute;
  top: -15px;
  left: 50%;
  width: 34px;
  height: 23px;
  margin-left: -17px;
  fill: #e0b01c;
  stroke: #7a5c05;
  stroke-width: 1.2;
  stroke-linejoin: round;
}

.mp_cream .mp_who {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  max-width: 100%;
  font-size: 17px;
  font-weight: bold;
}

.mp_cream .mp_who img {
  flex: none;
  width: 16px;
  height: 11px;
  box-shadow: 0 0 0 1px rgb(0 0 0 / 15%);
}

.mp_cream .mp_name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mp_cream .mp_tag {
  min-height: 16px;
  color: var(--accent-lo);
  font-size: 13px;
  font-weight: bold;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.mp_cream .mp_points {
  margin-top: 2px;
  font:
    400 48px/1.05 Saloon,
    Georgia,
    serif;
  font-variant-numeric: tabular-nums;
}

.mp_cream .mp_caption {
  color: #5c4a30;
  font-size: 14px;
}

.mp_cream .mp_meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
  color: #4a3a26;
  font-size: 14px;
}

.mp_cream .mp_badge {
  margin-top: 10px;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: bold;
}

.mp_cream .mp_badge_done {
  background: #dfeed6;
  color: #2b5418;
}

.mp_cream .mp_badge_playing {
  background: #dde9f5;
  color: #1c4a73;
}

.mp_cream .mp_badge_left {
  background: #f3dedb;
  color: #842619;
}







@keyframes mp-dot {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}

.mp_cream .mp_vs {
  align-self: center;
  color: #8a7656;
  font:
    400 22px/1 Saloon,
    Georgia,
    serif;
}

.mp_cream .mp_clock {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 20px;
  background: var(--pause-ink);
  color: #fff7e2;
  border-radius: 10px;
}

.mp_cream .mp_clock_label {
  font-size: 15px;
}

.mp_cream .mp_clock_time {
  font:
    400 30px/1 Saloon,
    Georgia,
    serif;
  font-variant-numeric: tabular-nums;
}

.mp_cream .mp_clock_low {
  background: #842619;
}

/* the countdown before the deal: the seconds in a cream disc that pops on every second */
.mp_cream .mp_count {
  display: grid;
  place-items: center;
  width: 92px;
  height: 92px;
  margin-top: -4px;
  background: #fffdf6;
  border: 3px solid var(--accent);
  border-radius: 50%;
  box-shadow: 0 3px 10px rgb(60 40 10 / 18%);
  color: var(--pause-ink);
  font:
    400 52px/1 Saloon,
    Georgia,
    serif;
  font-variant-numeric: tabular-nums;
  animation: pause-pop 500ms cubic-bezier(0.34, 1.5, 0.64, 1) both;
}

.mp_cream .mp_board_compact .mp_side {
  padding: 12px 10px;
}

.mp_cream .mp_game {
  color: #4a3a26;
  font-size: 16px;
}

.mp_cream .mp_rules {
  width: 100%;
  max-width: 520px;
  margin: 0;
  padding: 12px 16px 12px 34px;
  background: rgb(255 255 255 / 55%);
  border: 1px solid #ddd0b0;
  border-radius: 10px;
  color: #333;
  font-size: 15px;
  line-height: 1.45;
  text-align: left;
}

.mp_cream .mp_rules li + li {
  margin-top: 4px;
}

/* result window of v2: Lobby and New opponent white, Rematch the blue one, one row */
.lbx_window.mp_cream .win_buttons input {
  flex-basis: 220px;
  /* two lines where a language's text is long (owner 2026-10-30: "Czekamy na przeciwnika" was cut off) */
  padding-right: 10px;
  padding-left: 10px;
  white-space: normal;
  line-height: 1.15;
}

.lbx_window.mp_cream .win_buttons input.mp_btn_secondary {
  background: #fff;
  color: var(--accent-lo);
  box-shadow: inset 0 0 0 1px var(--accent);
}

/* ---- the v2 lobby (MultiplayerLobby.tsx, step 2): Quick play first, the tiles, the waiting players, a friend ---- */










.mp_games .mp_error,
.mp_cream .mp_note {
  padding: 8px 12px;
  background: #f3dedb;
  border-radius: 8px;
  color: #842619;
  font-size: 15px;
  text-align: center;
}

/* a note the player must act on ("The opponent wants a rematch"): the Rematch button's blue, white bold text (owner
   2026-10-31, iPad: "ten tekst jest słabo widoczny" on the pale blue) */
.mp_cream .mp_note {
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  font-size: 17px;
  padding: 10px 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}

.mp_games .mp_friend {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px;
  background: #fffdf6;
  border: 2px solid var(--accent);
  border-radius: 12px;
  text-align: center;
}

.mp_games .mp_friend_title {
  font-weight: bold;
}

.mp_games .mp_friend_code {
  font:
    400 44px/1.1 Saloon,
    Georgia,
    serif;
  letter-spacing: 0.12em;
}

.mp_games .mp_friend_link {
  color: var(--accent-lo);
  font-size: 15px;
  word-break: break-all;
}

.mp_games .mp_invite {
  min-height: 48px;
  padding: 8px 28px;
  background: #1f6f4a;
  border: 0;
  border-radius: 8px;
  color: #fff;
  font: inherit;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
}

.mp_games .mp_friend_alt {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin: 10px 0;
  font-size: 15px;
}

.mp_games .mp_friend_alt .mp_friend_code {
  font: inherit;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
}

.mp_games .mp_friend_copy {
  min-height: 44px;
  padding: 0 4px;
  background: none;
  border: 0;
  color: var(--link);
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.mp_games .mp_friend_preview {
  width: min(440px, 100%);
  margin: 0 auto 10px;
  text-align: left;
}

.mp_games .mp_friend_preview_cap {
  margin-bottom: 4px;
  color: #4a4a4a;
  font-size: 13px;
}

.mp_games .mp_friend_preview_box {
  display: flex;
  gap: 10px;
  padding: 8px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 10px;
  color: #222;
  font-size: 14px;
  line-height: 1.3;
}

.mp_games .mp_friend_preview_box img {
  flex: none;
  width: 96px;
  height: 64px;
  border-radius: 6px;
  object-fit: cover;
}

.mp_games .mp_friend_preview_box b,
.mp_games .mp_friend_preview_box span,
.mp_games .mp_friend_preview_box small {
  display: block;
}

.mp_games .mp_friend_preview_box small {
  margin-top: 3px;
  color: #666;
}

.mp_games .mp_room_code .mp_invite {
  display: block;
  width: min(320px, 100%);
  margin: 0 auto;
}

.mp_games .mp_room_code .mp_friend_alt {
  margin: 4px 0 0;
}

.mp_games .mp_friend_text {
  color: #4a3a26;
  font-size: 14px;
}

/* ---- the lobby, second look (owner 2026-10-29): the Games window's pattern, one row per game, Play at the bottom ---- */






.mp_cream ul.mp_games {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}







.mp_cream .mp_game_name {
  font-size: 18px;
  font-weight: bold;
}

.mp_cream .mp_game_desc {
  color: #4a3a26;
  font-size: 14px;
  font-weight: normal;
}









/* the radio mark of the Games window: a ring, filled blue when chosen */








/* the button rows of the cream windows: secondary white, the main action blue (the app's buttons) */
.lbx_window.mp_cream .rules_buttons input.mp_btn_primary,
.lbx_window.mp_cream .win_buttons input.mp_btn_primary {
  background: var(--accent);
  color: #fff;
  box-shadow: none;
}

.lbx_window.mp_cream .rules_buttons input.mp_btn_primary:hover {
  background: var(--accent-hi);
}





/* the leave window: the scores as one flat strip (not cards: they are not buttons), the help text, the buttons */
.mp_cream .mp_board_flat {
  padding: 10px 6px;
  background: rgb(255 255 255 / 55%);
  border-top: 1px solid #ddd0b0;
  border-bottom: 1px solid #ddd0b0;
  max-width: none;
}

.mp_cream .mp_board_flat .mp_side,
.mp_cream .mp_board_flat .mp_side.mp_me {
  padding: 0;
  background: none;
  border: 0;
  box-shadow: none;
}

.mp_cream .mp_board_flat .mp_points {
  font-size: 38px;
}

.mp_cream .mp_help {
  max-width: 540px;
  color: #333;
  font-size: 15px;
  line-height: 1.45;
  text-align: left;
}

.mp_cream .mp_help p {
  margin: 0 0 6px;
}

.lbx_window.lbx_multiplayer_leave .rules_buttons input {
  flex: 0 1 180px;
}

@media (max-width: 559px) {
  









/* the leave window: full-width buttons, the main action first */
  .lbx_window.lbx_multiplayer_leave .rules_buttons input {
    flex: 1 0 100%;
  }

  .lbx_window.lbx_multiplayer_leave .rules_buttons input.mp_btn_primary {
    order: -1;
  }

  /* Play first and full width on a phone */
  
}

/* ---- the lobby (variant A of the prototype round, status style 1; owner 2026-10-29): the Games window's own look ---- */
.mp_games .mp_counts {
  display: flex;
  align-items: center;
  margin-left: auto;
  color: #444;
  font-size: 14px;
}

/* margins, not flex gap: Safari 14.0 has no flex gap (.ai/gotchas.md) and the dot and the two numbers touched there */
.mp_games .mp_dot {
  margin-right: 6px;
  width: 9px;
  height: 9px;
  background: #3a8a2a;
  border-radius: 50%;
}

/* Quick play's list (owner 2026-11-01, variant O): production's rows without the marks; a game's rooms in white strips beside
   the picture, "Create a room" on the right */
.mp_games .mp_row {
  display: grid;
  grid-template-areas: 'pic name btn' 'pic rooms btn';
  /* the picture as tall as the name and one room: the first room does not stretch the row (owner 2026-11-01) */
  grid-template-columns: 135px minmax(0, 1fr) auto;
  grid-template-rows: auto 1fr;
  gap: 6px 18px;
  align-items: start;
  box-sizing: border-box;
  min-height: 94px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--win-hair);
  color: var(--text);
}

.mp_games .mp_row_pic {
  display: block;
  grid-area: pic;
  width: 135px;
  height: auto;
  border-radius: 6px;
}

.mp_games .mp_row .game_name {
  grid-area: name;
  align-self: start;
  font-size: 17px;
  line-height: 22px;
}

.mp_games .mp_rm_bots {
  margin-inline-start: 6px;
  color: #4a3a26;
  font-size: 13px;
  white-space: nowrap;
}

.mp_games .mp_rooms {
  display: flex;
  flex-direction: column;
  grid-area: rooms;
  gap: 4px;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mp_games .mp_rm {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 10px;
  box-sizing: border-box;
  min-height: 48px;
  /* the same gap above, beside and under the button (owner 2026-11-01) */
  padding: 4px 4px 4px 12px;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  font-size: 15px;
}

/* where a game has no room: a grey box as tall as a room's strip (owner's pick 2026-11-01); the white strips stand out */
.mp_games .mp_rm_none {
  display: flex;
  grid-area: rooms;
  align-items: center;
  box-sizing: border-box;
  min-height: 48px;
  margin: 0;
  padding: 4px 12px;
  background: rgb(0 0 0 / 5%);
  border-radius: 8px;
  color: #5f5f5f;
  font-size: 14px;
}

.mp_games .mp_rm_mine {
  border-color: #2b7a18;
}

.mp_games .mp_rm_info {
  display: flex;
  flex: 1 1 200px;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.mp_games .mp_rm_no {
  flex: none;
  min-width: 62px;
  color: #444;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.mp_games .mp_rm_who {
  display: flex;
  flex-wrap: wrap;
  gap: 0 12px;
  min-width: 0;
}

.mp_games .mp_rm_player {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.mp_games .mp_rm_player img {
  flex: none;
  width: 16px;
  height: 11px;
  margin-right: 6px;
  box-shadow: 0 0 0 1px rgb(0 0 0 / 15%);
}

.mp_games .mp_rm_player b {
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp_games .mp_rm_meta {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 10px;
  color: #444;
  font-size: 13px;
}

.mp_games .mp_rm_seats,
.mp_games .mp_rm_time {
  display: inline-flex;
  align-items: center;
}

.mp_games .mp_rm_time b {
  min-width: 30px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.mp_games .mp_rm_btn {
  flex: none;
  align-self: stretch;
  min-width: 92px;
  min-height: 38px;
  padding: 4px 12px;
  background: var(--accent);
  border: 0;
  border-radius: 5px;
  color: #fff;
  font: inherit;
  font-weight: bold;
  white-space: nowrap;
  cursor: pointer;
}

.mp_games .mp_rm_btn:hover {
  background: var(--accent-hi);
}

.mp_games .mp_rm_cancel,
.mp_games .mp_rm_cancel:hover {
  background: #e4e4e4;
  color: #222;
}

.mp_games .mp_create {
  grid-area: btn;
  box-sizing: border-box;
  min-width: 148px;
  max-width: 210px;
  min-height: 42px;
  padding: 6px 14px;
  background: #fff;
  border: 1px solid #b9c6d6;
  border-radius: 6px;
  color: #2f5f8f;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

/* the "+" of a new room (owner's pick A of 6, 2026-11-01) */
.mp_games .mp_create::before {
  content: '+';
  margin-right: 6px;
  font-size: 19px;
  line-height: 1;
  vertical-align: -1px;
}

.mp_games .mp_create:hover {
  background: #f1f5fa;
}

/* the row has as many rooms as the server allows: join one of them */
.mp_games .mp_create:disabled {
  background: #e4e4e4;
  border-color: #d0d0d0;
  color: #666;
  cursor: default;
}

/* phones and low screens: the picture beside the name, the rooms and the button under them, the whole width */
@media (max-width: 839px), (max-height: 614px) {
  .mp_games .mp_row {
    grid-template-areas: 'pic name' 'rooms rooms' 'btn btn';
    grid-template-columns: 88px minmax(0, 1fr);
    grid-template-rows: auto;
    gap: 6px 12px;
    min-height: 0;
    padding: 8px 12px;
  }

  .mp_games .mp_row_pic {
    width: 88px;
  }

  .mp_games .mp_row .game_name {
    font-size: 15px;
    line-height: 19px;
  }

  .mp_games .mp_create {
    width: 100%;
    max-width: none;
  }

  /* a phone's row is full already: the button stands under the picture */
  .mp_games .mp_rm_none {
    display: none;
  }

  .mp_games .mp_rm {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .mp_games .mp_rm_btn {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}

.mp_games .mp_minutes {
  color: #666;
  font-size: 0.85em;
  font-weight: normal;
}

.mp_games .mp_elsewhere {
  margin: 8px 16px 0;
  padding: 8px 12px;
  background: #fbf1d6;
  border-radius: 8px;
  color: #5c4708;
  font-size: 15px;
  text-align: center;
}

.mp_games .mp_code {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 16px;
  font-size: 17px;
}

.mp_games .mp_code input {
  width: 200px;
  padding: 10px;
  border: 1px solid #999;
  border-radius: 10px;
  font: inherit;
  font-size: 32px;
  letter-spacing: 0.25em;
  text-align: center;
  text-transform: uppercase;
}

.mp_games .mp_friend {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 24px 16px;
  padding: 16px;
  background: #fff;
  border: 2px solid var(--accent);
  border-radius: 12px;
  text-align: center;
}

.mp_games .mp_error {
  margin: 8px 16px 0;
  padding: 8px 12px;
  background: #f3dedb;
  border-radius: 8px;
  color: #842619;
  font-size: 15px;
  text-align: center;
}

.mp_games .game_button_cont {
  gap: 12px;
}

.lbx_window.lbx_multiplayer.mp_games .game_button_cont input.mp_btn_secondary {
  background: #fff;
  color: var(--accent-lo);
  box-shadow: inset 0 0 0 1px var(--accent);
}

/* the lobby while it connects (owner 2026-10-29: "popraw stylowanie ładowania"): no white box, the turning ring of
   our SVG suits (64 px) and the words under it, in the middle of the window's whole body */
.lbx_window.lbx_multiplayer.mp_games .games_wrapper:has(> .multi_loading) {
  display: flex;
  min-height: 420px;
}

.lbx_window.lbx_multiplayer.mp_games .multi_loading {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: auto;
  margin: 0;
  padding: 48px 16px;
  border: 0;
  border-radius: 0;
  background: none;
  color: var(--text-2);
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.mp_games .multi_loading_spin {
  margin-bottom: 22px;
}

.mp_games .multi_loading_suit {
  display: flex;
  align-items: center;
  justify-content: center;
}

.mp_games .multi_loading_suit .ht-suit {
  width: 24px;
  height: 24px;
  fill: #2b2b2b;
}

.mp_games .multi_loading_suit .ht-suit:is([data-suit='H'], [data-suit='D']) {
  fill: #c62828;
}

/* The Tables tab (owner 2026-10-31): every open table and every game in progress on one page, no row to click first.
   Margins, not flex gap (Safari 14.0, .ai/gotchas.md). */
/* the counts and the Tables pill, at the row's right edge; they wrap together on a narrow screen */
.mp_games .mp_sort_right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
}

.mp_games .mp_pill_tables {
  display: inline-flex;
  align-items: center;
  margin-left: 12px;
}

.mp_games .mp_pill_n {
  min-width: 20px;
  height: 20px;
  margin: 0 -6px 0 7px;
  padding: 0 5px;
  background: #2b7a18;
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  line-height: 20px;
  text-align: center;
}

.mp_games .mp_pill_tables.on .mp_pill_n {
  background: #fff;
  color: var(--accent);
}

.mp_games .mp_tb_lists {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  column-gap: 14px;
  padding: 2px 14px 10px;
}

.mp_games .mp_tb_col {
  min-width: 0;
}

.mp_games .mp_tb_head {
  display: flex;
  align-items: center;
  margin: 8px 0 6px;
  color: #333;
  font-size: 15px;
  font-weight: bold;
}

.mp_games .mp_tb_n {
  min-width: 20px;
  margin-left: 7px;
  padding: 0 5px;
  background: #dcdcdc;
  border-radius: 10px;
  color: #222;
  font-size: 13px;
  line-height: 20px;
  text-align: center;
}

.mp_games .mp_tb_lists ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* the picture; the title, a thin line, the players, the time and the seats; the button on the right */
.mp_games .mp_tb {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) auto;
  grid-template-areas: 'pic title btn' 'pic slots btn' 'pic meta btn';
  align-items: start;
  column-gap: 10px;
  margin-bottom: 6px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgb(0 0 0 / 8%);
  color: #222;
}

.mp_games .mp_tb_mine {
  background: #f1f7ec;
  border-color: #2b7a18;
}

.mp_games .mp_tb_running {
  grid-template-areas: 'pic title meta' 'pic slots slots' 'pic late late';
  background: #fbfaf7;
  box-shadow: none;
}

.mp_games .mp_tb_pic {
  display: block;
  grid-area: pic;
  width: 54px;
  height: 38px;
  border-radius: 4px;
  object-fit: cover;
}

.mp_games .mp_tb_title {
  display: flex;
  flex-wrap: wrap;
  grid-area: title;
  align-items: baseline;
  min-width: 0;
  padding-bottom: 4px;
  border-bottom: 1px solid #dcdcdc;
  line-height: 1.25;
}

.mp_games .mp_tb_fam {
  color: #111;
  font-size: 16px;
  font-weight: bold;
}

.mp_games .mp_tb_min {
  margin-left: 6px;
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}

.mp_games .mp_tb_min::before {
  content: '· ';
}

.mp_games .mp_tb_kind {
  flex-basis: 100%;
  color: #444;
  font-size: 13.5px;
}

.mp_games .mp_tb_slots {
  grid-area: slots;
  min-width: 0;
  margin-top: 5px;
}

.mp_games .mp_tb_slot {
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 15px;
  line-height: 1.5;
}

.mp_games .mp_tb_slot img {
  flex: none;
  width: 16px;
  height: 11px;
  margin-right: 6px;
  box-shadow: 0 0 0 1px rgb(0 0 0 / 15%);
}

.mp_games .mp_tb_name {
  overflow: hidden;
  min-width: 0;
  color: #333;
  font-size: 14.5px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp_games .mp_tb_name small {
  color: #666;
  font-size: 13px;
  font-weight: normal;
}

.mp_games .mp_tb_me {
  flex: none;
  margin-left: 6px;
  padding: 0 6px;
  background: #2b7a18;
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
}

.mp_games .mp_tb_private {
  color: #555;
  font-style: italic;
}

.mp_games .mp_tb_slot em {
  flex: none;
  min-width: 34px;
  margin-left: auto;
  padding: 0 7px;
  background: #ebeef1;
  border-radius: 9px;
  color: #222;
  font-size: 14px;
  font-style: normal;
  font-variant-numeric: tabular-nums;
  font-weight: bold;
  text-align: center;
}

.mp_games .mp_tb_lead .mp_tb_name {
  color: #7a5a00;
  font-weight: 600;
}

.mp_games .mp_tb_lead em {
  background: #f4d775;
  color: #4a3600;
}

.mp_games .mp_tb_meta {
  display: flex;
  flex-wrap: wrap;
  grid-area: meta;
  align-items: center;
  min-width: 0;
  margin-top: 3px;
  color: #444;
  font-size: 13px;
}

.mp_games .mp_tb_running .mp_tb_meta {
  margin-top: 0;
}

.mp_games .mp_tb_wait {
  display: inline-flex;
  align-items: center;
  margin-right: 12px;
  white-space: pre;
}

.mp_games .mp_tb_running .mp_tb_wait {
  margin-right: 0;
}

.mp_games .mp_tb_wait b {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.mp_games .mp_tb_icon {
  flex: none;
  width: 14px;
  height: 14px;
  margin-right: 4px;
  background: currentColor;
}

.mp_games .mp_tb_clock {
  mask: var(--i-clock) center / contain no-repeat;
}

/* lowercase "czeka" sits below the line's middle: half a pixel down puts the clock on its middle (owner 2026-11-01) */
.mp_games .mp_tb_wait .mp_tb_clock {
  position: relative;
  top: 0.5px;
}

.mp_games .mp_tb_seats {
  display: inline-flex;
  align-items: center;
}

.mp_games .mp_tb_seats small {
  margin-left: 3px;
  font-size: 13px;
  font-weight: bold;
}

/* a free seat a dotted ring, a taken one a token; a bot's token grey */
.mp_games .mp_tb_seat {
  width: 16px;
  height: 16px;
  margin-right: 2px;
  background: #6f6f6f;
  mask: var(--i-seat-free) center / contain no-repeat;
}

.mp_games .mp_tb_seat.on {
  background: #2b7a18;
  mask: var(--i-seat) center / contain no-repeat;
}

.mp_games .mp_tb_bot {
  width: 14px;
  height: 14px;
  margin-right: 8px;
  background: #8c8c8c;
  mask: var(--i-seat) center / contain no-repeat;
}

.mp_games .mp_tb_btn {
  grid-area: btn;
  align-self: center;
  min-height: 38px;
  padding: 6px 14px;
  background: var(--accent);
  border: 0;
  border-radius: 6px;
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: bold;
  white-space: nowrap;
  cursor: pointer;
}

.mp_games .mp_tb_late {
  grid-area: late;
  justify-self: stretch;
  margin-top: 6px;
  min-height: 44px;
}

.mp_games .mp_tb_btn:hover {
  background: var(--accent-hi);
}

.mp_games .mp_tb_cancel,
.mp_games .mp_tb_cancel:hover {
  background: #e4e4e4;
  color: #222;
}

.mp_games .mp_tb_none {
  margin: 0 0 6px;
  padding: 12px;
  background: #f6f6f6;
  border-radius: 8px;
  color: #444;
  font-size: 15px;
  text-align: center;
}

/* phones and low screens: one column, the tables that wait first; the minutes may wrap under a long name */
@media (max-width: 839px), (max-height: 614px) {
  .mp_games .mp_tb_lists {
    grid-template-columns: minmax(0, 1fr);
    padding: 0 10px 10px;
  }

  .mp_games .mp_tb_fam {
    margin-right: 6px;
  }

  .mp_games .mp_tb_min {
    margin-left: 0;
  }

  .mp_games .mp_tb_min::before {
    content: '';
  }
}

/* the waiting room of a card table: the game, the four seats, who wants to start */
.mp_games .mp_room {
  padding: 14px 18px 6px;
}

.mp_games .mp_room_head {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.mp_games .mp_room_head .game_preview {
  flex: none;
  width: 120px;
  height: auto;
  margin-right: 16px;
  border-radius: 6px;
}

.mp_games .mp_room_head .game_name {
  font-size: 19px;
  font-weight: bold;
}

.mp_games .mp_room_count {
  margin-top: 4px;
  color: #333;
  font-size: 15px;
}

.mp_games .mp_room_code {
  margin: 10px 0 12px;
  text-align: center;
}

/* a phone, a long language: a button's words go on two lines, they are never cut (owner 2026-11-02: "stylowanie przycisków") */
@media (max-width: 559px) {
  .lbx_window.lbx_multiplayer.mp_games .game_button_cont input {
    height: auto;
    min-height: 52px;
    padding: 4px 6px;
    white-space: normal;
    line-height: 1.15;
  }
}

.mp_games .mp_seats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mp_games .mp_seat {
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  font-size: 15px;
}

.mp_games .mp_seat > * + * {
  margin-left: 8px;
}

.mp_games .mp_seat img {
  width: 16px;
  height: 11px;
  box-shadow: 0 0 0 1px rgb(0 0 0 / 15%);
}

.mp_games .mp_seat_me {
  border: 2px solid var(--accent);
}

.mp_games .mp_seat .mp_badge {
  padding: 1px 8px;
  background: var(--accent);
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
}

.mp_games .mp_seat_state {
  margin-left: auto;
  color: #666;
  font-size: 13px;
  text-align: right;
}

.mp_games .mp_seat_state.on {
  padding: 2px 10px;
  background: #dfeed6;
  border-radius: 999px;
  color: #2b5418;
  font-weight: bold;
}

.mp_games .mp_seat_free {
  background: #f4f4f4;
  border-style: dashed;
  color: #666;
}

.mp_games .mp_seat_free b {
  font-weight: normal;
}

.mp_games .mp_seat_bot {
  flex: none;
  width: 20px;
  height: 20px;
  background: #9a9a9a;
  mask: var(--i-user-circle) center / contain no-repeat;
}

.mp_games .mp_room_note {
  margin: 12px 0 4px;
  color: #333;
  font-size: 15px;
  text-align: center;
}

.mp_games .mp_room_wait {
  color: #7a5a00;
}

.lbx_window.lbx_multiplayer.mp_games .game_button_cont input:disabled {
  opacity: 0.45;
  cursor: default;
}

@media (max-width: 599px) {
  .mp_games .mp_seats {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .mp_games .mp_seat {
    min-height: 40px;
    padding: 5px 10px;
  }

  .mp_games .mp_room {
    padding: 10px 12px 4px;
  }

  .mp_games .mp_room_note {
    margin-top: 8px;
    font-size: 14px;
  }

  .mp_games .mp_room_head .game_preview {
    width: 84px;
  }
}

/* phones and low screens: the multiplayer windows are full-screen sheets like the others (owner's rule); their
   fit-content height above would otherwise win over the sheet's height */
@media (max-width: 839px), (max-height: 614px) {
  .lbx_window.lbx_multiplayer.mp_cream,
  .lbx_window.lbx_multiplayer_leave,
  .lbx_window.lbx_multiplayer_done,
  .lbx_window.lbx_multiplayer_win.mp_cream {
    height: min(100dvh, var(--vvh, 100dvh));
  }
}

/* the bar after Quick play (the player keeps a solo game going): one slim line under the top bar's centre; on a phone at the bottom above the dock, where the board has room (at the top it hid the
   stock) */
.mp_searching {
  position: fixed;
  top: 40px; /* under the top bar, above the foundations (they start near 100 px) */
  left: 50%;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: calc(100vw - 32px);
  padding: 5px 6px 5px 12px;
  background: #fff7e2;
  border: 1px solid #d6c7a2;
  border-radius: 999px;
  box-shadow: 0 3px 10px rgb(0 0 0 / 30%);
  color: #2b1a0b;
  font-size: 15px;
  white-space: nowrap;
  transform: translateX(-50%);
}

.mp_searching_text {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.mp_flag {
  display: inline-block;
  width: 16px;
  height: 11px;
  margin-inline-end: 4px;
  vertical-align: -1px;
}

.mp_searching_lines {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.mp_searching_other {
  color: #4a3a26;
  font-size: 13px;
  white-space: normal;
}

.mp_searching .mp_searching_other button {
  padding: 0;
  background: none;
  box-shadow: none;
  border-radius: 0;
  color: var(--link);
  text-decoration: underline;
}

.mp_searching_two {
  border-radius: 18px;
}

.mp_searching_text b {
  overflow: hidden;
  text-overflow: ellipsis;
}

.mp_searching_text i {
  flex: none;
  font-style: normal;
  font-variant-numeric: tabular-nums;
}

.mp_searching_text small {
  flex: 0 1 auto;
  overflow: hidden;
  color: #4a3a26;
  font-size: 13px;
  text-overflow: ellipsis;
}

.mp_searching_spin {
  flex: none;
  width: 18px;
  height: 18px;
  border: 3px solid #e3d5b0;
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: mp-spin 0.9s linear infinite;
}

@keyframes mp-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 559px) {
  .mp_searching {
    top: auto;
    bottom: calc(var(--ad-anchor-h) + var(--dock-h, 56px) + 10px);
  }

  .mp_searching_text small {
    display: none;
  }
}

/* a card table's notice (online/CardNotice.tsx): the searching bar's look, wrapped text, the "I'm back" bar warm */
.mp_notice {
  white-space: normal;
  text-align: center;
}

/* a sentence of two or three lines on a phone: rounded corners, not the one-line pill's oval, even padding (owner
   2026-11-02); the "I'm back" bar keeps its pill with the button */
.mp_notice:not(.mp_asleep) {
  padding: 7px 14px;
  border-radius: 16px;
}

/* one flowing sentence with the flag inline before the name: the bar's flex row made the flag, the name and the rest
   separate boxes, and the flag floated in a corner */
.mp_notice .mp_searching_text {
  display: block;
}

.mp_asleep {
  background: #ffe3a6;
  border-color: #c99a2e;
}

/* "your room closed" says all of it, on two lines if it must (max-content: a box at left 50 % would shrink to half the screen) */
.mp_searching_closed {
  box-sizing: border-box;
  width: max-content;
  padding-left: 16px;
  border-radius: 18px;
}

.mp_searching_closed .mp_searching_text b {
  overflow: visible;
  white-space: normal;
  text-overflow: clip;
}

.mp_searching button {
  flex: none;
  margin: 0;
  padding: 5px 12px;
  border-radius: 999px;
  background: #fff;
  border: 0;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #3d7ab3;
  color: #2a5f8f;
  font: inherit;
  font-weight: bold;
  cursor: pointer;
}



@media (max-width: 559px) {
  .mp_cream .mp_cont {
    padding: 16px 14px 12px;
  }

  .mp_cream .mp_points {
    font-size: 40px;
  }

  .mp_cream .mp_title {
    font-size: 26px;
  }
}

/* Delete account (owner 2026-10-25: hidden enough that no older player hits it by a slip): a small grey link at the
   bottom of the Panel's player box, far from Logout; its window (AccountWindows.tsx DeleteAccountWindow) uses the
   Confirm window's look with Send outlined and greyed out until the tick box is ticked, Cancel the big blue one. */
.dashboard_cont .delete_account_link {
  margin: 10px 0 0;
  text-align: right;
  font-size: 12px;
}
.dashboard_cont .delete_account_link a {
  color: #666; /* 5.7:1 on the box's #e6e6e6 */
  text-decoration: underline;
}
.dashboard_cont .delete_account_link a:hover {
  color: #222;
}
.lbx_delete_account .delete_account_sure {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-weight: 700;
}
.lbx_delete_account .delete_account_sure input {
  width: 22px;
  height: 22px;
  margin: 0;
  flex: none;
}
.lbx_delete_account .delete_account_failed {
  margin-top: 10px;
  color: #b00020;
}
.lbx_delete_account .rules_buttons input:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
/* after the send only OK is left: the filled blue one, not the Confirm window's outlined first button */
.lbx_delete_account .rules_buttons input:only-child {
  background: var(--accent);
  color: #fff;
  box-shadow: none;
}

/* No moves on a low screen (SimpleWindows.tsx useFitWindow): each step while the window still scrolls (owner
   2026-10-26: on a phone held sideways its buttons were off the screen) */
.lbx_window.lbx_no_moves[data-fit-1] .nomoves_hero {
  zoom: 0.6;
}
.lbx_window.lbx_no_moves[data-fit-2] .nomoves_hero {
  display: none;
}
.lbx_window.lbx_no_moves[data-fit-3] .nomoves_cont {
  gap: 4px;
  padding-top: 8px;
  padding-bottom: 6px;
}
.lbx_window.lbx_no_moves[data-fit-3] .nomoves_title {
  font-size: 20px;
}
.lbx_window.lbx_no_moves[data-fit-3] .nomoves_text {
  font-size: 14px;
  line-height: 1.3;
}
.lbx_window.lbx_no_moves[data-fit-4] .rules_buttons {
  flex-wrap: nowrap;
  gap: 8px;
  padding-top: 6px;
  padding-bottom: 6px;
}
.lbx_window.lbx_no_moves[data-fit-4] .rules_buttons input {
  flex: 1 1 0;
  min-width: 0;
  height: 40px;
  line-height: 40px;
}

/* Mahjong's shape in the Games window (owner 2026-10-27, variant C of prototype/mj-shape-picker): a small pill in the
   Mahjong row, under its text, opens a list of the shapes over the window (the games below do not move); the chosen
   one in the tabs' blue */
.games_window .mj_entry {
  border-bottom: 1px solid var(--win-hair);
}
.games_window .mj_entry > .option_item_entry {
  border-bottom: 0;
}
/* the hover shades the row and the pill's line under it as one (owner 2026-10-28, iPad: only the row above the pill
   was shaded) */
.games_window .mj_entry:not(.selected):hover {
  background: rgb(0 0 0 / 4%);
}
.games_window .mj_entry > .option_item_entry:hover {
  background: none;
}
/* the chosen Mahjong: the row and the pill's line under it one grey block */
.radio .mj_entry.selected {
  background: var(--win-row-sel);
  box-shadow: var(--win-row-press);
}
.radio .mj_entry.selected > .option_item_entry {
  background: none;
  box-shadow: none;
}
/* the pill: a line of its own under the row (laid over the row it covered the name or the badge in the compact and
   one-line rows; owner 2026-10-27, iPad in a small window), starting where the row's text starts in each layout */
.games_window .mj_shape_pill {
  position: relative;
  display: block;
  height: 32px;
  margin: -6px 0 12px 198px;
  padding: 0 32px 0 13px;
  border: 1.5px solid #bdbdbd;
  border-radius: 16px;
  background: #fff;
  color: var(--text-2);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
@media (max-width: 839px), (max-height: 614px) {
  .games_window .mj_shape_pill {
    margin: -4px 0 12px 16px;
  }
}
/* the one-line rows of a low window (owner 2026-10-27: "mniejsza pastylka"): a smaller pill, its line slimmer */
.lbx_window.lbx_games[data-ad-tight] .games_window .mj_shape_pill {
  height: 24px;
  margin: -6px 0 6px 88px;
  padding: 0 24px 0 10px;
  border-width: 1px;
  border-radius: 12px;
  font-size: 12px;
}
.lbx_window.lbx_games[data-ad-tight] .games_window .mj_shape_pill::after {
  right: 8px;
  top: 6px;
  width: 11px;
  height: 11px;
}
.games_window .mj_shape_pill:hover {
  border-color: #999;
}
.games_window .mj_shape_pill strong {
  color: var(--text);
}
.games_window .mj_shape_pill::after {
  content: '';
  position: absolute;
  right: 10px;
  top: 8px;
  width: 14px;
  height: 14px;
  background: var(--text-3);
  mask: var(--i-chevron-down) center / contain no-repeat;
}
/* the list: a layer over the page (GamesWindow.tsx, a portal), placed from the pill; it scrolls inside itself when
   the screen is low */
.mj_shapes_catch {
  position: fixed;
  inset: 0;
  z-index: 10000;
}
.mj_shapes {
  position: fixed;
  z-index: 10001;
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 6px 24px rgb(0 0 0 / 35%);
  font-family: var(--font, inherit);
}
.mj_shape {
  display: grid;
  grid-template-columns: 54px 1fr;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 4px 8px;
  border: 0;
  border-radius: 6px;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.mj_shape:hover {
  background: #f0f0f0;
}
.mj_shape.selected {
  background: var(--accent);
  color: #fff;
  font-weight: 700;
}
.mj_shape .mj_shape_outline {
  grid-row: span 2;
  width: 46px;
  height: 36px;
}
/* the die's left edge on the shapes' left edge (owner 2026-10-27: it stood further right, centred) */
.mj_shape .mj_dice {
  width: 36px;
  height: 36px;
  justify-self: start;
}
/* 3-5-8's lengths (owner 2026-10-28: the same list as the shapes): the number of deals in a circle where a shape's
   outline stands */
.mj_shape .mj_length_num {
  display: grid;
  grid-row: span 2;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 2px solid currentColor;
  border-radius: 50%;
  font-size: 15px;
  font-weight: 700;
  opacity: 0.8;
}
/* Tysiąc's 500 and 1000: a smaller number in the same circle */
.mj_shape .mj_length_num[data-wide] {
  font-size: 11px;
  letter-spacing: -0.3px;
}
/* the name in the middle of its row, beside the picture (owner 2026-10-28: it stood at the top): alone it takes both
   grid rows; with a note under it, the two sit together round the middle */
.mj_shape .mj_shape_name {
  align-self: end;
}
.mj_shape .mj_shape_name:last-child {
  grid-row: span 2;
  align-self: center;
}
.mj_shape .mj_shape_note {
  align-self: start;
}
.mj_shape_note {
  color: var(--text-3);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
}
.mj_shape.selected .mj_shape_note {
  color: #e6eef6;
}
/* a phone: the pill and the list from the row's left edge */

/* Spider one suit's colours (owner 2026-10-28: the same list as the shapes): the suit dealt, the game's own icon
   (hearts/suits.tsx), where a shape's outline stands; white on the picked row */
.mj_shape .mj_colour_suit {
  display: grid;
  grid-row: span 2;
  place-items: center;
  width: 36px;
  height: 36px;
}
.mj_shape .mj_colour_suit .ht-suit {
  width: 30px;
  height: 30px;
  fill: #1d1d1d;
}
.mj_shape .mj_colour_suit .ht-suit[data-suit='H'] {
  fill: #c62828;
}
.mj_shape.selected .mj_colour_suit .ht-suit {
  fill: #fff;
}

/* ---- the quick messages at the end of an online game (online/QuickSay.tsx) ---- */
.mp_say_row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  width: 100%;
}

.mp_say {
  /* the emoji and the words centred on one line (owner 2026-11-02: "wyrównaj tekst w przyciskach"): the emoji's glyph is
     taller than the letters and pushed the words off the middle */
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 4px 12px;
  background: #fff;
  border: 1px solid var(--accent);
  border-radius: 18px;
  color: var(--accent-lo);
  font-size: 14px;
  line-height: 1.2;
  cursor: pointer;
  transition: transform 80ms ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.mp_say > span {
  font-size: 16px;
  line-height: 1;
}

.mp_say:hover:not(:disabled) {
  background: #eaf2fa;
}

/* pressed: on a touch screen :active alone often does not show (QuickSay keeps data-pressed a moment) */
.mp_say:active:not(:disabled),
.mp_say[data-pressed]:not(:disabled) {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
  transform: scale(0.96);
}

.mp_say:disabled {
  border-color: #c9c2b2;
  color: #6f6a5e;
  cursor: default;
}

.mp_said {
  --said-x: -50%;

  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  z-index: 5;
  width: max-content;
  max-width: 210px;
  padding: 7px 12px;
  background: #fff;
  border: 1px solid #b9a877;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgb(60 40 10 / 25%);
  color: #2b2118;
  font-size: 15px;
  line-height: 1.25;
  text-align: left;
  pointer-events: none;
  animation: mp-said-in 220ms ease-out both;
}

/* a newer bubble came, or the time is up: it flies up and fades, as hearts on a stream */
.mp_said[data-leaving] {
  z-index: 4;
  animation: mp-said-out 750ms ease-out both;
}

.mp_said::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  width: 10px;
  height: 10px;
  margin: -5px 0 0 -5px;
  background: #fff;
  border-right: 1px solid #b9a877;
  border-bottom: 1px solid #b9a877;
  transform: rotate(45deg);
}

/* the race's sides: the tail ends just above the player's name, the name stays whole (owner 2026-10-30, iPhone and iPad:
   with the bubble over the top of the card a part of the name showed under it) */
.mp_side .mp_said {
  bottom: calc(100% - 9px);
}

/* the outer tiles and sides: the bubble stays inside the window */
.hs-tile:first-child .mp_said,
.mp_side:first-child .mp_said {
  --said-x: 0;

  left: 0;
}

.hs-tile:last-child .mp_said,
.mp_side:last-child .mp_said {
  --said-x: 0;

  right: 0;
  left: auto;
}

.hs-tile:first-child .mp_said::after,
.mp_side:first-child .mp_said::after {
  left: 28px;
}

.hs-tile:last-child .mp_said::after,
.mp_side:last-child .mp_said::after {
  right: 28px;
  left: auto;
}

@keyframes mp-said-in {
  from {
    opacity: 0;
    transform: translate(var(--said-x), 12px) scale(0.9);
  }

  to {
    opacity: 1;
    transform: translate(var(--said-x), 0) scale(1);
  }
}

@keyframes mp-said-out {
  from {
    opacity: 1;
    transform: translate(var(--said-x), 0) scale(1);
  }

  to {
    opacity: 0;
    transform: translate(var(--said-x), -56px) scale(0.92);
  }
}

@media (max-width: 559px) {
  .lbx_window.mp_cream.lbx_multiplayer_win .win_buttons input {
    flex: 1 1 100%;
    white-space: normal;
  }

  .lbx_window.mp_cream.lbx_multiplayer_win .win_buttons input.mp_btn_secondary {
    flex: 1 1 40%;
    min-width: 0;
    padding-right: 6px;
    padding-left: 6px;
    font-size: 15px;
  }
}

.lbx_window.lbx_multiplayer_win[data-fit-1] .mp_cont {
  gap: 8px;
  padding: 10px 14px 8px;
}

.lbx_window.lbx_multiplayer_win[data-fit-2] .mp_sub {
  display: none;
}

.lbx_window.lbx_multiplayer_win[data-fit-2] .mp_title {
  font-size: 24px;
}

.lbx_window.lbx_multiplayer_win[data-fit-3] .mp_side {
  padding: 8px 8px 6px;
}

.lbx_window.lbx_multiplayer_win[data-fit-3] .mp_points {
  font-size: 34px;
}

.lbx_window.lbx_multiplayer_win[data-fit-4] .mp_meta {
  display: none;
}

.lbx_window.lbx_multiplayer_win[data-fit-4] .mp_say {
  min-height: 32px;
  font-size: 13px;
}

.lbx_window.lbx_multiplayer_win[data-fit-3] .mp_side .mp_said {
  bottom: calc(100% - 3px); /* the card's padding is smaller */
}

/* the card games' tiles: as the race's sides; once the window has dropped its picture and made the tiles small, the tile
   is at the top of the window: the bubble stays inside */
.hs-tile .mp_said {
  bottom: calc(100% - 4px);
}

.hs-cont .mp_say_row {
  margin-top: 10px;
}

.lbx_window[data-fit-4] .hs-tile .mp_said {
  top: 2px;
  bottom: auto;
}

.lbx_window[data-fit-4] .hs-tile .mp_said::after {
  display: none;
}

@media (max-width: 559px) {
  .lbx_window[data-fit-4] .hs-tile:nth-child(odd) .mp_said {
    --said-x: 0;

    right: auto;
    left: 0;
  }

  .lbx_window[data-fit-4] .hs-tile:nth-child(even) .mp_said {
    --said-x: 0;

    right: 0;
    left: auto;
  }
}

.lbx_window[data-fit-3] .hs-cont .mp_say_row {
  margin-top: 4px;
}

/* ---- Dashboard: the Details tab (owner 2026-10-31) and the Panel's own donut (ui/svgCharts.tsx) ---- */
.dashboard_panel .dashboard_main .rows:last-child .row {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.dashboard_panel .most_played_label {
  flex: none;
}

.dashboard_panel .viz_donut {
  flex: 1 1 auto;
  min-height: 150px;
}

.details_panel {
  text-align: left;
  font-size: 15px;
  color: var(--text);
}

/* the section pills stay in view while the section scrolls (the same pills as the Games window's sort row) */
.details_pills {
  position: sticky;
  top: 0;
  z-index: 1;
}

.details_body {
  padding: 14px;
}

.details_body > .details_box:last-child,
.details_body > .details_grid:last-child {
  margin-bottom: 0;
}

.details_table th {
  white-space: normal;
}

.details_note {
  padding: 40px 20px;
  text-align: center;
  font-size: 15px;
  font-style: italic;
  color: var(--text-2);
}

.details_tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.details_grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}

.details_box {
  box-sizing: border-box;
  min-width: 0;
  padding: 12px 16px 14px;
  background: var(--win-box);
  border-radius: var(--r-box);
}

.details_wide {
  grid-column: 1 / -1;
}

.details_box h3 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text);
}

.details_box h4 {
  margin: 12px 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-2);
}

.details_sub {
  margin: -4px 0 8px;
  font-size: 13px;
  color: var(--text-3);
}

.details_table_box .details_sub {
  margin: 8px 0 0;
}

.details_donut {
  min-height: 230px;
}

.details_donut .viz_donut {
  height: auto;
}

.details_meter2 {
  margin-top: 14px;
}

.details_variants {
  margin-top: 4px;
}

.details_table_box {
  margin-bottom: 14px;
}

.details_table_scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.details_table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  white-space: nowrap;
}

.details_table th,
.details_table td {
  padding: 7px 10px;
  text-align: left;
  border-bottom: 1px solid rgb(0 0 0 / 8%);
}

.details_table th {
  font-weight: 600;
  color: var(--text-2);
  font-size: 13px;
}

.details_table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.details_table tbody tr:hover td {
  background: rgb(255 255 255 / 45%);
}

.details_table a {
  color: var(--link);
}

.details_with {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.details_person {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #fff;
  border: 1px solid #cfcfcf;
  border-radius: 999px;
  white-space: nowrap;
}

.details_person b {
  margin-inline-start: 8px;
  font-variant-numeric: tabular-nums;
}

.details_person_bot {
  background: transparent;
  color: #4a4a4a;
}

.details_person_win {
  border-color: #1f6f4a;
  box-shadow: inset 0 0 0 1px #1f6f4a;
}

.details_person_win b {
  color: #1f6f4a;
}

/* a phone: each game a small block, no table to swipe: the game and the result, the date and my points, the opponents */
@media (max-width: 559px) {
  .details_table[data-table="online-recent"],
  .details_table[data-table="online-recent"] tbody {
    display: block;
  }

  .details_table[data-table="online-recent"] thead {
    display: none;
  }

  .details_table[data-table="online-recent"] tr {
    display: grid;
    grid-template-areas: 'game result' 'when you' 'with with';
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 3px 10px;
    padding: 9px 2px;
    border-bottom: 1px solid var(--win-hair);
  }

  .details_table[data-table="online-recent"] td {
    padding: 0;
    border: 0;
  }

  .details_table[data-table="online-recent"] td:nth-child(1) { grid-area: when; font-size: 13px; }
  .details_table[data-table="online-recent"] td:nth-child(2) { grid-area: game; font-weight: 700; }
  .details_table[data-table="online-recent"] td:nth-child(3) { grid-area: result; text-align: end; }
  .details_table[data-table="online-recent"] td:nth-child(4) { grid-area: with; margin-top: 3px; }
  .details_table[data-table="online-recent"] td:nth-child(5) { grid-area: you; font-size: 13px; }

  .details_table[data-table="online-recent"] td.num::before {
    content: attr(data-label) ": ";
  }

  .details_person {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.details_result_win {
  color: #1f6f4a;
  font-weight: 700;
}

.details_result_loss {
  color: #a3261c;
}

.details_dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 8px;
  vertical-align: -1px;
}

@media (max-width: 839px) {
  .details_tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .details_grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 559px) {
  .details_body {
    padding: 10px;
  }

  .details_box {
    padding: 10px 12px 12px;
  }

  .details_tiles,
  .details_grid {
    gap: 8px;
    margin-bottom: 10px;
  }

  .viz_tile_value {
    font-size: 20px;
  }
}

/* ---- Dashboard: the Summary as rows (owner 2026-10-31: "ładniejsze ostylowanie, więcej informacji") ---- */
.panel_stats {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}

.dashboard_details ul.panel_stats li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: 'label value' 'note value';
  column-gap: 12px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgb(0 0 0 / 8%);
}

.panel_stats li:last-child {
  border-bottom: 0;
}

.panel_stats .ps_label {
  grid-area: label;
  font-size: 14px;
  color: var(--text-2);
}

.panel_stats .ps_value {
  grid-area: value;
  font-size: 22px;
  font-weight: 700;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.panel_stats .ps_note {
  grid-area: note;
  font-size: 13px;
  color: var(--text-3);
}

.panel_more {
  margin: 10px 0 0;
  text-align: right;
  font-size: 14px;
}

.panel_more a {
  color: var(--link);
  font-weight: 600;
}

.panel_more a::after {
  content: ' \203A';
}

@media (min-width: 560px) and (max-height: 820px) {
  .dashboard_details ul.panel_stats li {
    padding: 5px 0;
  }

  .panel_stats .ps_value {
    font-size: 19px;
  }
}

/* ---- Flags: rows with the country's name and a search box (owner 2026-10-31) ---- */
.flags_window {
  display: flex;
  flex-direction: column;
}

.flags_search {
  flex: none;
  padding: 10px 16px;
  background: var(--win-panel);
  border-bottom: 1px solid var(--win-border);
}

.flags_search input {
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 1px solid var(--win-border);
  border-radius: 20px;
  background: #fff;
  color: var(--text);
  font: inherit;
  font-size: 16px;
}

.flags_search input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 2px rgb(61 122 173 / 25%);
}

.lbx_flags .option_item {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 2px 10px;
  padding: 8px 12px;
}

.lbx_flags .radio .option_item_entry {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  width: auto;
  min-height: 44px;
  padding: 4px 10px;
  border: 0;
  border-radius: 8px;
  color: var(--text);
  font-size: 15px;
}

.lbx_flags .radio .option_item_entry img {
  flex: none;
  width: 32px;
  height: 24px;
  border-radius: 3px;
  box-shadow: 0 0 0 1px rgb(0 0 0 / 12%);
  object-fit: cover;
}

.lbx_flags .flag_name {
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.lbx_flags .radio .option_item_entry.selected {
  background: var(--win-row-sel);
  box-shadow: inset 4px 0 0 var(--win-row-bar);
}

.flags_none {
  grid-column: 1 / -1;
  margin: 20px 0;
  text-align: center;
  color: var(--text-3);
}

@media (max-width: 559px) {
  .lbx_flags .option_item {
    grid-template-columns: minmax(0, 1fr);
    padding: 4px 8px;
  }
}

/* the Menu's "how many wait online" (owner 2026-11-01, pick A of ?mw on branch prototype/menu-online-count-2026-11-01):
   a person and the number in the text's colour. In the row of buttons it is a subscript in the low right corner, out of
   the label's flow: the label keeps its size and the button its width. */
#menu_content > a.menu_multiplayer {
  position: relative;
}

/* "with people" under the label: two lines in the button's height, the count stays in its corner */
.menu_online {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
}

.menu_online::after {
  content: attr(data-sub);
  font-size: 0.78em;
  line-height: 1.05;
  white-space: nowrap;
}

.menu_waiting {
  position: absolute;
  right: 4px;
  bottom: 3px;
  display: inline-flex;
  align-items: center;
  gap: 1px;
  color: inherit;
  font-size: 9.5px;
  font-weight: 700;
  line-height: 1;
}

.menu_waiting i {
  display: block;
  width: 9px;
  height: 9px;
  background: currentColor;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='7' r='5'/%3E%3Cpath d='M2 23c0-6 4.500-9 10-9s10 3 10 9z'/%3E%3C/svg%3E") center / contain no-repeat;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='7' r='5'/%3E%3Cpath d='M2 23c0-6 4.500-9 10-9s10 3 10 9z'/%3E%3C/svg%3E") center / contain no-repeat;
}

/* the menu as a sheet (app.css, SHEET_TIER in App.tsx): its rows have room: a normal icon and number at the row's right */
@media (max-width: 839px), (max-height: 500px) {
  .menu_online {
    align-items: flex-start;
  }

  .menu_online::after {
    font-size: 13px;
    line-height: 15px;
  }

  .menu_waiting {
    top: 50%;
    right: 20px;
    bottom: auto;
    gap: 6px;
    font-size: inherit;
    font-weight: 400;
    transform: translateY(-50%);
  }

  .menu_waiting i {
    width: 18px;
    height: 18px;
  }
}
